<template>
  <div
    ref="workspace"
    :class="['restaurante-checkout', { 'is-fullscreen': isFullscreen }]"
  >
    <div class="card card-custom gutter-b">
      <div class="card-body">
        <div v-if="!checkout" class="empty-state">
          Carregando dados do checkout.
        </div>

        <template v-else>
          <b-row class="checkout-layout">
            <b-col lg="8">
              <div class="checkout-header-block">
                <div class="checkout-title">
                  <div>
                    <h3>Checkout Restaurante</h3>
                    <span>Atendimento #{{ atendimentoId }}</span>
                  </div>
                  <button
                    type="button"
                    class="checkout-fullscreen-button"
                    :title="
                      isFullscreen ? 'Sair da tela cheia' : 'Usar tela cheia'
                    "
                    :aria-label="
                      isFullscreen ? 'Sair da tela cheia' : 'Usar tela cheia'
                    "
                    @click="toggleFullscreen"
                  >
                    <i
                      :class="[
                        'fas',
                        isFullscreen ? 'fa-compress' : 'fa-expand',
                      ]"
                      aria-hidden="true"
                    />
                  </button>
                </div>

                <div class="checkout-summary">
                  <div>
                    <span>
                      {{ modoApenasComandasCheckout ? "Comanda" : "Mesa" }}
                    </span>
                    <strong>{{ mesaLabel }}</strong>
                  </div>
                  <div>
                    <span>Ambiente</span>
                    <strong>{{ ambienteLabel }}</strong>
                  </div>
                  <div>
                    <span>Tempo aberto</span>
                    <strong>
                      {{ checkout.atendimento.tempo_aberto || "-" }}
                    </strong>
                  </div>
                  <div>
                    <span>Pendente</span>
                    <strong>
                      {{ formatMoney(currentTotals.valor_pendente) }}
                    </strong>
                  </div>
                </div>

                <div class="checkout-flow">
                  <div
                    v-for="step in checkoutSteps"
                    :key="step.label"
                    :class="[
                      'checkout-flow-step',
                      { active: step.active, done: step.done },
                    ]"
                  >
                    <span></span>
                    <strong>{{ step.label }}</strong>
                  </div>
                </div>
              </div>

              <div
                v-if="checkout.alertas && checkout.alertas.length"
                class="mb-5"
              >
                <div
                  v-for="alerta in checkout.alertas"
                  :key="alerta"
                  class="alert alert-warning mb-2"
                >
                  {{ alerta }}
                </div>
              </div>

              <div class="checkout-section mb-6">
                <div class="section-title">
                  <div>
                    <h4>Comandas</h4>
                    <span>{{ checkoutComandasSubtitle }}</span>
                  </div>
                  <button
                    v-if="canVincularAvulsaMesa"
                    type="button"
                    class="btn btn-sm btn-light-primary font-weight-bold"
                    @click="openVincularAvulsaModal"
                  >
                    Adicionar avulsa
                  </button>
                </div>
                <div class="checkout-comandas-grid">
                  <button
                    v-if="!modoApenasComandasCheckout"
                    type="button"
                    :class="[
                      'checkout-comanda',
                      { active: form.escopo === 'mesa' },
                    ]"
                    :disabled="dividindoCobranca"
                    @click="selectMesaInteira"
                  >
                    <strong>Mesa inteira</strong>
                    <span>{{ mesaLabel }}</span>
                    <span
                      >Pendente
                      {{ formatMoney(baseTotals.valor_pendente) }}</span
                    >
                  </button>

                  <button
                    v-for="comanda in checkout.comandas"
                    :key="comanda.id"
                    type="button"
                    :class="[
                      'checkout-comanda',
                      {
                        active:
                          form.escopo === 'comanda' &&
                          form.comanda_id === comanda.id,
                      },
                    ]"
                    :disabled="dividindoCobranca"
                    @click="selectComanda(comanda.id)"
                  >
                    <strong>{{ comandaLabel(comanda) }}</strong>
                    <span>{{
                      comanda.pessoa ? comanda.pessoa.nome : "Sem cliente"
                    }}</span>
                    <span>
                      Pendente {{ formatMoney(comanda.valor_pendente) }}
                    </span>
                    <span v-if="Number(comanda.valor_reservado || 0) > 0">
                      Reservado {{ formatMoney(comanda.valor_reservado) }}
                    </span>
                    <span v-else>
                      Já cobrado {{ formatMoney(comanda.valor_ja_cobrado) }}
                    </span>
                  </button>
                </div>
              </div>

              <div class="checkout-dividir-cobranca">
                <div class="checkout-dividir-titulo">
                  Dividir cobrança por pessoas
                </div>
                <div class="checkout-dividir-controles">
                  <input
                    v-model.number="dividirPessoas"
                    type="number"
                    min="2"
                    max="20"
                    class="form-control form-control-sm checkout-dividir-input"
                    placeholder="Nº pessoas"
                    :disabled="dividindoCobranca"
                  />
                  <button
                    v-if="!dividindoCobranca"
                    type="button"
                    class="btn btn-light-primary btn-sm font-weight-bold"
                    :disabled="!podeDividirCobranca"
                    @click="confirmarDividirCobranca"
                  >
                    Preparar divisão
                  </button>
                  <div v-else class="d-flex align-items-center flex-wrap">
                    <span class="font-weight-bold text-primary mr-3">
                      Pessoa {{ dividindoPessoaAtual }} de
                      {{ dividindoTotalPessoas }} preparada
                    </span>
                    <button
                      type="button"
                      class="btn btn-light-danger btn-sm font-weight-bold"
                      @click="cancelarDivisaoCobranca"
                    >
                      Cancelar divisão
                    </button>
                  </div>
                </div>
                <small class="text-muted" v-if="!dividindoCobranca">
                  Prepara os itens igualmente, uma pessoa por vez. Nenhuma
                  cobrança ou nota é gerada automaticamente.
                </small>
                <small v-else class="text-muted">
                  Ajuste taxa, couvert ou preços, revise o preview e escolha
                  pré-venda ou NFC-e. A próxima pessoa será preparada somente
                  após a confirmação deste pagamento.
                </small>
              </div>

              <div class="checkout-tables-grid">
                <div class="checkout-section">
                  <div class="section-title">
                    <h4>{{ checkoutItensTitle }}</h4>
                    <span>{{ itensPendentes.length }} com saldo</span>
                  </div>

                  <div v-if="!checkout.itens.length" class="blocked-state">
                    Atendimento sem itens válidos para cobrança.
                  </div>

                  <div v-else class="table-responsive itens-pendentes-scroll">
                    <table
                      class="table table-head-custom table-vertical-center table-compact"
                    >
                      <thead>
                        <tr>
                          <th>Produto</th>
                          <th class="text-right">Total pendente</th>
                          <th class="text-right">Ação</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="item in checkout.itens"
                          :key="item.id"
                          :class="{
                            muted: Number(item.quantidade_pendente) <= 0,
                          }"
                        >
                          <td>
                            <strong>{{ produtoDescricao(item) }}</strong>
                            <span class="d-block text-muted font-size-sm">
                              {{ itemComandaOrigem(item) }}
                            </span>
                          </td>
                          <td class="text-right">
                            <strong>
                              {{ formatMoney(item.valor_pendente) }}
                            </strong>
                            <span class="d-block text-muted font-size-sm">
                              {{ formatQuantity(item.quantidade_pendente) }}
                            </span>
                          </td>
                          <td class="text-right">
                            <button
                              type="button"
                              class="btn btn-sm btn-light-primary font-weight-bold"
                              :class="{
                                'btn-light-success':
                                  itemActionLabel(item) === 'Pago',
                                'btn-light-warning':
                                  itemActionLabel(item) === 'Reservado',
                              }"
                              :disabled="!canAddItem(item)"
                              @click="addItemCobranca(item)"
                            >
                              {{ itemActionLabel(item) }}
                            </button>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <div class="checkout-section">
                  <div class="section-title">
                    <h4>Itens da cobrança</h4>
                    <span>{{ cobrancaItensDetalhados.length }} item(ns)</span>
                  </div>

                  <div v-if="!cobrancaItens.length" class="empty-mini">
                    Selecione itens pendentes para montar a cobrança.
                  </div>

                  <div v-else class="table-responsive cobranca-itens-scroll">
                    <table
                      class="table table-head-custom table-vertical-center table-compact"
                    >
                      <thead>
                        <tr>
                          <th>Produto</th>
                          <th class="text-right">Qtd</th>
                          <th class="text-right">Valor</th>
                          <th class="text-right">Ação</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="linha in cobrancaItensDetalhados"
                          :key="linha.atendimento_item_id"
                        >
                          <td>
                            <strong>{{ produtoDescricao(linha.item) }}</strong>
                            <span
                              v-if="linha.ajuste_valor > 0"
                              :class="[
                                'ajuste-chip',
                                linha.ajuste_operacao === 'acrescimo'
                                  ? 'ajuste-chip-acrescimo'
                                  : 'ajuste-chip-desconto',
                              ]"
                            >
                              {{ ajusteItemLabel(linha) }}
                            </span>
                          </td>
                          <td class="cobranca-quantidade-cell">
                            <b-form-input
                              v-model.number="linha.quantidade"
                              min="0.0001"
                              :max="linha.item.quantidade_pendente"
                              step="0.0001"
                              type="number"
                              :disabled="dividindoCobranca"
                              @input="updateQuantidade(linha)"
                            />
                          </td>
                          <td class="text-right font-weight-bold">
                            {{ formatMoney(valorLinhaCobranca(linha)) }}
                          </td>
                          <td class="text-right cobranca-acoes-cell">
                            <div class="item-action-buttons">
                              <button
                                type="button"
                                class="btn btn-sm btn-light-danger btn-hover-danger btn-icon btn-ajuste-item mr-2"
                                v-b-tooltip.hover="'Ajuste do preço do item'"
                                @click="openAjusteItemModal(linha)"
                              >
                                <Cash :size="17" />
                              </button>
                              <button
                                type="button"
                                class="btn btn-sm btn-danger btn-hover-danger btn-icon"
                                :disabled="dividindoCobranca"
                                @click="
                                  removeItemCobranca(linha.atendimento_item_id)
                                "
                              >
                                <TrashCanIcon :size="16" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div
                v-if="checkout.fechamentos.length"
                class="checkout-section mt-6"
              >
                <div class="section-title">
                  <h4>Cobranças</h4>
                  <span>{{ checkout.fechamentos.length }} registro(s)</span>
                </div>
                <div class="cobrancas-list">
                  <div
                    v-for="fechamento in checkout.fechamentos"
                    :key="fechamento.id"
                    class="cobranca-line"
                  >
                    <div>
                      <strong
                        >#{{ fechamento.id }} - {{ fechamento.status }}</strong
                      >
                      <span>{{ fechamentoLabel(fechamento) }}</span>
                    </div>
                    <div class="text-right">
                      <strong>{{
                        formatMoney(valorFechamentoExibicao(fechamento))
                      }}</strong>
                      <button
                        v-if="canPagarFechamento(fechamento)"
                        type="button"
                        class="btn btn-sm btn-success font-weight-bold d-block mt-2"
                        @click="selecionarPagamentoFechamento(fechamento)"
                      >
                        Pagar
                      </button>
                      <button
                        v-if="canCancelarRascunho(fechamento)"
                        type="button"
                        class="btn btn-sm btn-light-danger font-weight-bold d-block mt-2"
                        :disabled="cancelandoRascunho"
                        @click="cancelarRascunho(fechamento)"
                      >
                        {{
                          cancelandoRascunho
                            ? "Cancelando..."
                            : "Cancelar rascunho"
                        }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div
                v-if="checkout.itens_cancelados.length"
                class="checkout-section mt-6"
              >
                <div class="section-title">
                  <h4>Itens cancelados</h4>
                  <span>Não entram na cobrança</span>
                </div>
                <div class="cancelled-list">
                  <div
                    v-for="item in checkout.itens_cancelados"
                    :key="item.id"
                    class="cancelled-item"
                  >
                    <strong>{{ produtoDescricao(item) }}</strong>
                    <span>{{ formatQuantity(item.quantidade) }}</span>
                    <span>{{ formatMoney(item.valor_total) }}</span>
                  </div>
                </div>
              </div>
            </b-col>

            <b-col lg="4">
              <div class="checkout-actions">
                <button
                  type="button"
                  class="btn btn-light-dark font-weight-bolder font-size-sm"
                  @click="voltarMesas"
                >
                  Voltar para Mesas
                </button>
                <button
                  type="button"
                  class="btn btn-dark font-weight-bolder font-size-sm"
                  @click="getCheckout"
                >
                  Atualizar
                </button>
              </div>

              <div class="checkout-panel">
                <div class="section-title">
                  <h4>PDV Restaurante</h4>
                  <span>Cobrança</span>
                </div>

                <b-form-group
                  label="Comanda pagadora"
                  label-for="pagador_checkout"
                >
                  <b-form-select
                    id="pagador_checkout"
                    v-model="form.pagador"
                    :options="cobrancaPagadoraOptions"
                    :disabled="dividindoCobranca"
                    @change="selectPagador"
                  />
                </b-form-group>

                <div class="checkout-adjustments">
                  <div>
                    <label for="taxa_servico">Taxa serviço %</label>
                    <b-form-input
                      id="taxa_servico"
                      v-model.number="taxaServicoPercentual"
                      min="0"
                      step="0.01"
                      type="number"
                      @input="resetEstadoCobranca"
                    />
                  </div>
                  <div>
                    <label for="couvert_valor">Couvert</label>
                    <b-form-input
                      id="couvert_valor"
                      v-model.number="couvertValor"
                      min="0"
                      step="0.01"
                      type="number"
                      @input="resetEstadoCobranca"
                    />
                  </div>
                </div>

                <div class="totals-box">
                  <div>
                    <span>{{ currentTotalsLabel }}</span>
                    <strong>
                      {{ formatMoney(currentTotals.valor_total) }}
                    </strong>
                  </div>
                  <div>
                    <span>Já cobrado</span>
                    <strong>
                      {{ formatMoney(currentTotals.valor_ja_cobrado) }}
                    </strong>
                  </div>
                  <div>
                    <span>Reservado</span>
                    <strong>{{
                      formatMoney(currentTotals.valor_reservado)
                    }}</strong>
                  </div>
                  <div>
                    <span>Pendente</span>
                    <strong>{{
                      formatMoney(currentTotals.valor_pendente)
                    }}</strong>
                  </div>
                  <div>
                    <span>Subtotal produtos</span>
                    <strong>{{ formatMoney(subtotalProdutosCobranca) }}</strong>
                  </div>
                  <div>
                    <span>Taxa serviço</span>
                    <strong>{{ formatMoney(valorTaxaServico) }}</strong>
                  </div>
                  <div>
                    <span>Couvert</span>
                    <strong>{{ formatMoney(valorCouvertTotal) }}</strong>
                  </div>
                  <div>
                    <span>Acréscimo</span>
                    <strong>{{ formatMoney(valorAcrescimoCobranca) }}</strong>
                  </div>
                  <div class="total-main">
                    <span>Total da cobrança</span>
                    <strong>
                      {{ formatMoney(totalCobrancaExibicao) }}
                    </strong>
                  </div>
                </div>

                <button
                  type="button"
                  class="btn btn-light-primary btn-block font-weight-bold mt-5"
                  :disabled="!canPreview"
                  @click="previewCheckout"
                >
                  Revisar valores
                </button>
                <button
                  type="button"
                  class="btn btn-primary btn-block font-weight-bold mt-3"
                  :disabled="!canCriarRascunho"
                  @click="criarRascunho"
                >
                  Preparar cobrança
                </button>
                <button
                  type="button"
                  class="btn btn-primary btn-block font-weight-bold mt-3"
                  :disabled="!canFinalizarPreVenda"
                  @click="finalizarCobranca('pre_venda')"
                >
                  {{ gerarPreVendaLabel }}
                </button>
                <button
                  v-if="featureFlags.nfceCheckout"
                  type="button"
                  class="btn btn-light-success btn-block font-weight-bold mt-3"
                  :disabled="!canFinalizarNfce"
                  @click="finalizarCobranca('nfce')"
                >
                  {{ gerarNfceLabel }}
                </button>
              </div>

              <div v-if="rascunhoCobrancaAtual" class="checkout-panel mt-6">
                <div class="section-title">
                  <h4>Cobrança preparada</h4>
                </div>
                <p class="mb-1">Cobrança #{{ rascunhoCobrancaAtual.id }}</p>
                <p class="mb-0 text-muted">
                  Pronta para enviar ao PDV ou revisar novamente.
                </p>
                <div class="checkout-draft-actions mt-4">
                  <button
                    v-if="canCancelarRascunho(rascunhoCobrancaAtual)"
                    type="button"
                    class="btn btn-light-danger font-weight-bold"
                    :disabled="cancelandoRascunho"
                    @click="cancelarRascunho(rascunhoCobrancaAtual)"
                  >
                    {{
                      cancelandoRascunho ? "Cancelando..." : "Cancelar rascunho"
                    }}
                  </button>
                  <button
                    type="button"
                    class="btn btn-light-primary font-weight-bold"
                    :disabled="imprimindoPreview"
                    @click="imprimirPreviewRascunho(rascunhoCobrancaAtual)"
                  >
                    {{
                      imprimindoPreview
                        ? "Gerando preview..."
                        : "Imprimir preview"
                    }}
                  </button>
                </div>
              </div>

              <div v-if="preVendaGerada" class="checkout-panel mt-6">
                <div class="section-title">
                  <h4>Pagamento liberado</h4>
                </div>
                <p class="mb-1">Pré-venda #{{ preVendaGerada.pre_venda.id }}</p>
                <p class="mb-0 text-muted">
                  O checkout foi atualizado sem abrir o PDV normal.
                </p>
                <button
                  type="button"
                  class="btn btn-primary btn-block font-weight-bold mt-4"
                  @click="selecionarPagamento(rascunhoCobrancaAtual)"
                >
                  Finalizar pré-venda
                </button>
              </div>
            </b-col>
          </b-row>
        </template>
      </div>
    </div>

    <b-modal
      id="modal-pagamento-restaurante"
      modal-class="restaurante-workspace-modal restaurante-checkout-payment-modal"
      :title="pagamentoModalTitle"
      centered
      scrollable
      size="lg"
      body-class="restaurante-payment-modal-body"
      :hide-header-close="dividindoCobranca"
      :no-close-on-backdrop="dividindoCobranca"
      :no-close-on-esc="dividindoCobranca"
      @hidden="onPagamentoModalHidden"
    >
      <b-row class="restaurante-payment-form-row align-items-end">
        <b-col md="5">
          <div class="font-weight-bolder font-size-lg mb-3">
            Forma de Pagamento
          </div>
          <b-form-select
            v-model="pagamentoForm.forma_pagamento_id"
            :options="formaPagamentoOptions"
          />
        </b-col>
        <b-col md="4">
          <div class="font-weight-bolder font-size-lg mb-3">Valor</div>
          <b-form-input
            ref="valorPagamentoRestaurante"
            v-model.number="pagamentoForm.valor"
            min="0.01"
            step="0.01"
            type="number"
            @keyup.enter="adicionarPagamento"
          />
        </b-col>
        <b-col md="3">
          <b-button
            variant="primary"
            class="restaurante-payment-add-button"
            :disabled="!canAdicionarPagamento"
            @click="adicionarPagamento"
          >
            (Enter) Adicionar
          </b-button>
        </b-col>
      </b-row>

      <div class="restaurante-payment-shortcuts mt-4">
        <button
          v-for="atalho in atalhosPagamento"
          :key="atalho.tecla"
          type="button"
          class="restaurante-payment-shortcuts__item"
          @click="adicionarPagamentoPorAtalho(atalho)"
        >
          <strong>{{ atalho.tecla }}</strong>
          <span>{{ atalho.descricao }}</span>
        </button>
      </div>

      <div v-if="pagamentos.length" class="restaurante-payment-list mt-4">
        <div
          v-for="(pagamento, index) in pagamentos"
          :key="`${pagamento.forma_pagamento_id}-${index}`"
          class="restaurante-payment-item"
        >
          <div>
            <strong>{{ pagamento.descricao }}</strong>
            <span>Pagamento {{ index + 1 }}</span>
          </div>
          <strong>{{ formatMoney(pagamento.valor) }}</strong>
          <button
            type="button"
            class="btn btn-icon btn-light-danger btn-sm"
            @click="removerPagamento(index)"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>
      </div>

      <div class="restaurante-payment-summary">
        <span>TOTAL</span>
        <strong>{{ formatMoney(pagamentoFechamentoValor) }}</strong>
        <small>Total de Itens: {{ pagamentoFechamentoItens }}</small>
        <div class="payment-balance-grid">
          <div>
            <span>Pago</span>
            <strong>{{ formatMoney(totalPago) }}</strong>
          </div>
          <div>
            <span>Restante</span>
            <strong>{{ formatMoney(restantePagamento) }}</strong>
          </div>
          <div>
            <span>Troco</span>
            <strong>{{ formatMoney(trocoPagamento) }}</strong>
          </div>
        </div>
      </div>

      <template #modal-footer="{ cancel }">
        <b-button v-if="!dividindoCobranca" variant="link" @click="cancel()">
          Cancelar
        </b-button>
        <span v-else class="text-muted mr-auto">
          Finalize o pagamento para continuar a divisão.
        </span>
        <b-button
          ref="finishModalRestaurante"
          variant="primary"
          :disabled="!canConfirmarPagamento"
          @click="confirmarPagamento"
        >
          {{ confirmarPagamentoLabel }}
        </b-button>
      </template>
    </b-modal>

    <b-modal
      id="modal-abertura-caixa-restaurante"
      modal-class="restaurante-workspace-modal restaurante-checkout-compact-modal"
      title="Abertura de caixa"
      centered
      hide-header-close
      no-close-on-backdrop
      no-close-on-esc
      scrollable
      size="md"
      @shown="focusValorAberturaCaixa"
    >
      <b-row>
        <b-col md="7">
          <b-form-group label="Valor Caixa R$" label-for="caixa_valor_abertura">
            <b-form-input
              id="caixa_valor_abertura"
              ref="caixaValorAberturaRestaurante"
              v-model.number="caixaValorAbertura"
              min="0"
              step="0.01"
              type="number"
              @keyup.enter="abrirCaixaCheckout"
            />
          </b-form-group>
        </b-col>
        <b-col md="5">
          <b-form-group label="Imprimir comprovante">
            <span class="switch switch-icon">
              <label>
                <input v-model="caixaImpressaoComprovante" type="checkbox" />
                <span></span>
              </label>
            </span>
          </b-form-group>
        </b-col>
      </b-row>

      <template #modal-footer="{ cancel }">
        <b-button
          variant="link"
          :disabled="abrindoCaixa"
          @click="cancelarAberturaCaixa(cancel)"
        >
          Cancelar
        </b-button>
        <b-button
          variant="primary"
          :disabled="abrindoCaixa"
          @click="abrirCaixaCheckout"
        >
          {{ abrindoCaixa ? "Abrindo..." : "Abrir caixa" }}
        </b-button>
      </template>
    </b-modal>

    <b-modal
      id="modal-ajuste-item-restaurante"
      modal-class="restaurante-workspace-modal restaurante-checkout-compact-modal"
      title="Ajuste no item"
      centered
      scrollable
      size="sm"
      @shown="focusAjusteItemInput"
    >
      <div>
        <p class="font-weight-bolder mb-1">
          {{ ajusteItemModal.produto }}
        </p>
        <p class="text-muted mb-4">
          Unitário atual: {{ formatMoney(ajusteItemModal.precoAtual) }}
        </p>

        <b-form-group label="Operação">
          <div class="d-flex">
            <b-form-checkbox
              class="mr-4"
              :checked="ajusteItemModal.operacao === 'desconto'"
              @change="setOperacaoAjusteItem('desconto')"
            >
              Desconto
            </b-form-checkbox>
            <b-form-checkbox
              :checked="ajusteItemModal.operacao === 'acrescimo'"
              @change="setOperacaoAjusteItem('acrescimo')"
            >
              Acréscimo
            </b-form-checkbox>
          </div>
        </b-form-group>

        <b-form-group label="Tipo do valor">
          <b-form-radio-group
            v-model="ajusteItemModal.tipo"
            :options="tiposAjusteItem"
            buttons
            button-variant="outline-primary"
            size="sm"
          />
        </b-form-group>

        <b-form-group :label="ajusteItemModalLabel">
          <b-input-group>
            <template #prepend>
              <b-input-group-text>
                {{ ajusteItemModal.tipo === 2 ? "%" : "R$" }}
              </b-input-group-text>
            </template>
            <b-form-input
              ref="ajusteItemInput"
              v-model="ajusteItemModal.valor"
              autocomplete="off"
              type="text"
              @keyup.enter="aplicarAjusteItem"
            />
          </b-input-group>
          <small class="text-muted">
            Novo unitário: {{ formatMoney(precoUnitarioComAjusteItem) }}
          </small>
        </b-form-group>
      </div>

      <template #modal-footer="{ cancel }">
        <b-button @click="cancel()">Cancelar</b-button>
        <b-button variant="primary" @click="aplicarAjusteItem">
          Aplicar
        </b-button>
      </template>
    </b-modal>

    <b-modal
      id="modal-vincular-avulsa-checkout"
      modal-class="restaurante-workspace-modal restaurante-checkout-compact-modal"
      title="Adicionar comanda avulsa"
      centered
      scrollable
      size="sm"
      @shown="focusCodigoAvulsaMesa"
    >
      <b-form-group label="Código da comanda" label-for="codigo_avulsa_mesa">
        <b-form-input
          id="codigo_avulsa_mesa"
          ref="codigoAvulsaMesa"
          v-model.trim="avulsaMesaForm.codigo"
          autocomplete="off"
          placeholder="Digite ou leia o código"
          @keyup.enter="vincularAvulsaMesa"
        />
      </b-form-group>

      <template #modal-footer="{ cancel }">
        <b-button variant="link" @click="cancel()">Cancelar</b-button>
        <b-button
          variant="primary"
          :disabled="vinculandoAvulsa"
          @click="vincularAvulsaMesa"
        >
          {{ vinculandoAvulsa ? "Adicionando..." : "Adicionar" }}
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import Cash from "vue-material-design-icons/Cash.vue";
import TrashCanIcon from "vue-material-design-icons/TrashCan.vue";
import { restauranteFeatureFlags } from "../../config";
import { imprimirPdfQz, imprimirTextoQz } from "../../services/qzTrayPrinter";

export default {
  name: "app-restaurante-checkout",
  components: {
    Cash,
    TrashCanIcon,
  },
  data() {
    return {
      checkout: null,
      preview: null,
      rascunho: null,
      criandoRascunho: false,
      previewing: false,
      gerandoPreVenda: false,
      gerandoNfce: false,
      confirmandoPagamento: false,
      cancelandoRascunho: false,
      imprimindoPreview: false,
      preVendaGerada: null,
      nfeGerada: null,
      pagamentoFechamento: null,
      tipoPagamentoCobranca: "pre_venda",
      vinculandoAvulsa: false,
      aberturaCaixaObrigatoria: null,
      abrindoCaixa: false,
      isFullscreen: false,
      fullscreenHandler: null,
      gerarPreVendaPendenteAbertura: false,
      tipoCobrancaPendenteAbertura: null,
      caixaValorAbertura: 0,
      caixaImpressaoComprovante: false,
      restauranteImpressaoAtiva: false,
      restauranteImpressoraCheckout: null,
      restauranteImpressoraComprovante: null,
      serieNfce: 99,
      informacoesComplementaresNfce: "",
      avulsaMesaForm: {
        codigo: "",
      },
      featureFlags: restauranteFeatureFlags,
      formasPagamento: [],
      pagamentos: [],
      pagamentoForm: {
        forma_pagamento_id: null,
        valor: 0,
      },
      ajusteItemModal: {
        atendimento_item_id: null,
        produto: "",
        operacao: "desconto",
        tipo: 2,
        valor: "",
        precoAtual: 0,
      },
      cobrancaItens: [],
      cobrancaIdempotencyToken: null,
      dividirPessoas: 2,
      dividindoCobranca: false,
      dividindoPessoaAtual: 0,
      dividindoTotalPessoas: 0,
      divisaoPartes: [],
      divisaoToken: null,
      divisaoTaxaServicoOriginal: 0,
      divisaoCouvertOriginal: 0,
      divisaoCobrancaItensOriginal: [],
      form: {
        escopo: "mesa",
        comanda_id: null,
        pagador: "mesa",
      },
      taxaServicoPercentual: 0,
      couvertValor: 0,
      atalhosPagamento: [
        { tecla: "F1", descricao: "C. Débito", termos: ["débito", "debito"] },
        {
          tecla: "F2",
          descricao: "C. Crédito",
          termos: ["crédito", "credito"],
        },
        { tecla: "F3", descricao: "Pix", termos: ["pix"] },
        { tecla: "F4", descricao: "À vista", termos: ["à vista", "a vista"] },
      ],
      tiposAjusteItem: [
        { value: 1, text: "Valor R$" },
        { value: 2, text: "Percentual" },
      ],
    };
  },
  computed: {
    ...mapGetters(["itsblocked"]),
    atendimentoId() {
      return this.checkout?.atendimento?.id || this.$route.params.atendimentoId;
    },
    codigoComandaRoute() {
      return (
        this.$route.params.codigoComanda ||
        this.$route.query.codigo_comanda ||
        null
      );
    },
    checkoutComandaIdRoute() {
      return this.$route.query.comanda_id || null;
    },
    modoApenasComandasCheckout() {
      return this.$route.query.modo_comandas === "1";
    },
    checkoutComandasSubtitle() {
      if (this.modoApenasComandasCheckout) {
        return `${this.checkout.comandas.length} comanda(s) aberta(s)`;
      }

      return `Mesa inteira + ${this.checkout.comandas.length} comanda(s)`;
    },
    checkoutItensTitle() {
      return this.form.escopo === "comanda" || this.modoApenasComandasCheckout
        ? "Itens pendentes da comanda"
        : "Itens pendentes da mesa";
    },
    mesaLabel() {
      if (!this.checkout || !this.checkout.mesa) {
        if (
          this.checkout?.checkout_origem === "comanda_avulsa" ||
          this.modoApenasComandasCheckout
        ) {
          const comanda = (this.checkout?.comandas || []).find(
            (item) => Number(item.id) === Number(this.form.comanda_id)
          );

          return comanda ? this.comandaLabel(comanda) : "Comanda";
        }

        return "-";
      }

      const vinculadas = this.checkout.mesa.mesas_vinculadas || [];

      if (!vinculadas.length) {
        return `Mesa ${this.checkout.mesa.numero}`;
      }

      return `Mesa ${this.checkout.mesa.numero} + ${vinculadas
        .map((mesa) => mesa.numero)
        .join(", ")}`;
    },
    ambienteLabel() {
      return (
        this.checkout?.ambiente?.nome ||
        this.checkout?.mesa?.ambiente_nome ||
        "-"
      );
    },
    canVincularAvulsaMesa() {
      return (
        !this.modoApenasComandasCheckout && Boolean(this.checkout?.mesa?.id)
      );
    },
    baseTotals() {
      return (
        this.checkout?.totais || {
          valor_total: 0,
          valor_ja_cobrado: 0,
          valor_reservado: 0,
          valor_pendente: 0,
        }
      );
    },
    selectedComanda() {
      if (this.form.escopo !== "comanda" || !this.form.comanda_id) {
        return null;
      }

      return (this.checkout?.comandas || []).find(
        (comanda) => Number(comanda.id) === Number(this.form.comanda_id)
      );
    },
    currentTotals() {
      if (!this.selectedComanda) {
        return this.baseTotals;
      }

      return {
        valor_total: Number(this.selectedComanda.valor_total || 0),
        valor_ja_cobrado: Number(this.selectedComanda.valor_ja_cobrado || 0),
        valor_reservado: Number(this.selectedComanda.valor_reservado || 0),
        valor_pendente: Number(this.selectedComanda.valor_pendente || 0),
      };
    },
    currentTotalsLabel() {
      return this.selectedComanda ? "Total da comanda" : "Total da mesa";
    },
    fechamentoConvertivelAtual() {
      const statuses = ["rascunho", "preview", "aguardando_conversao", "erro"];

      return (this.checkout?.fechamentos || []).find((fechamento) => {
        if (
          fechamento.pre_venda_id ||
          fechamento.venda_id ||
          fechamento.nfe_id ||
          !statuses.includes(fechamento.status)
        ) {
          return false;
        }

        if (this.form.escopo === "comanda") {
          return (
            Number(fechamento.comanda_id || 0) ===
            Number(this.form.comanda_id || 0)
          );
        }

        return !fechamento.comanda_id;
      });
    },
    rascunhoAtivo() {
      if (!this.rascunho) {
        return this.fechamentoConvertivelAtual || null;
      }

      if (this.form.escopo === "comanda") {
        return Number(this.rascunho.comanda_id || 0) ===
          Number(this.form.comanda_id || 0)
          ? this.rascunho
          : this.fechamentoConvertivelAtual || null;
      }

      return !this.rascunho.comanda_id
        ? this.rascunho
        : this.fechamentoConvertivelAtual || null;
    },
    rascunhoCobrancaAtual() {
      return this.dividindoCobranca ? this.rascunho : this.rascunhoAtivo;
    },
    itensPendentes() {
      return (this.checkout?.itens || []).filter(
        (item) =>
          Number(item.quantidade_pendente || 0) > 0 &&
          Number(item.valor_pendente || 0) > 0 &&
          item.cobravel !== false
      );
    },
    podeDividirCobranca() {
      const n = Math.floor(Number(this.dividirPessoas || 0));
      return (
        !this.dividindoCobranca &&
        n >= 2 &&
        n <= 20 &&
        this.cobrancaItens.length > 0
      );
    },
    cobrancaPagadoraOptions() {
      return [
        ...(!this.modoApenasComandasCheckout
          ? [{ value: "mesa", text: "Mesa inteira" }]
          : []),
        ...(this.checkout?.comandas || [])
          .filter((comanda) => comanda.status === "aberta")
          .map((comanda) => ({
            value: `comanda:${comanda.id}`,
            text: this.comandaLabel(comanda),
          })),
      ];
    },
    formaPagamentoOptions() {
      return [
        { value: null, text: "Selecione a forma" },
        ...this.formasPagamento.map((forma) => ({
          value: forma.id,
          text: forma.descricao,
        })),
      ];
    },
    cobrancaItensDetalhados() {
      return this.cobrancaItens
        .map((linha) => {
          const item = (this.checkout?.itens || []).find(
            (item) => item.id === linha.atendimento_item_id
          );

          if (!item) {
            return null;
          }

          return {
            ...linha,
            item,
          };
        })
        .filter(Boolean);
    },
    subtotalCobrancaLocal() {
      return this.cobrancaItensDetalhados.reduce(
        (total, linha) => total + this.valorLinhaCobranca(linha),
        0
      );
    },
    totaisPreviewCobranca() {
      return (
        this.preview?.totais_cobranca ||
        this.rascunhoCobrancaAtual?.totais_cobranca ||
        {}
      );
    },
    subtotalProdutosCobranca() {
      const subtotalBackend = this.pickNumber(this.totaisPreviewCobranca, [
        "valor_produtos",
        "subtotal_produtos",
        "subtotal",
        "valor_subtotal",
      ]);

      if (this.subtotalCobrancaLocal > 0) {
        return this.roundCurrency(this.subtotalCobrancaLocal);
      }

      return this.roundCurrency(
        subtotalBackend ??
          this.pickNumber(this.rascunhoCobrancaAtual, [
            "valor_produtos",
            "subtotal_produtos",
            "valor_subtotal",
            "valor_total",
          ]) ??
          0
      );
    },
    taxaServicoPercentualAtual() {
      return (
        this.pickNumber(this.totaisPreviewCobranca, [
          "taxa_servico_percentual",
          "percentual_taxa_servico",
        ]) ?? Number(this.taxaServicoPercentual || 0)
      );
    },
    couvertValorAtual() {
      return (
        this.pickNumber(this.totaisPreviewCobranca, [
          "couvert_valor",
          "valor_couvert_unitario",
        ]) ?? Number(this.couvertValor || 0)
      );
    },
    valorTaxaServicoBackend() {
      return this.pickNumber(this.totaisPreviewCobranca, [
        "valor_taxa_servico",
        "taxa_servico",
        "taxa_servico_valor",
      ]);
    },
    valorCouvertBackend() {
      return this.pickNumber(this.totaisPreviewCobranca, [
        "valor_couvert",
        "couvert",
        "couvert_total",
      ]);
    },
    possuiAjusteLocal() {
      return (
        Number(this.taxaServicoPercentual || 0) > 0 ||
        Number(this.couvertValor || 0) > 0
      );
    },
    valorTaxaServicoLocal() {
      return this.roundCurrency(
        this.subtotalProdutosCobranca *
          (Number(this.taxaServicoPercentual || 0) / 100)
      );
    },
    valorTaxaServico() {
      if (Number(this.taxaServicoPercentual || 0) > 0) {
        return this.valorTaxaServicoLocal;
      }

      return this.valorTaxaServicoBackend ?? 0;
    },
    valorCouvertLocal() {
      return this.roundCurrency(Number(this.couvertValor || 0));
    },
    valorCouvertTotal() {
      if (Number(this.couvertValor || 0) > 0) {
        return this.valorCouvertLocal;
      }

      return this.valorCouvertBackend ?? 0;
    },
    valorAcrescimoCobranca() {
      return this.roundCurrency(this.valorTaxaServico + this.valorCouvertTotal);
    },
    totalCobrancaLocal() {
      return this.roundCurrency(
        this.subtotalProdutosCobranca + this.valorAcrescimoCobranca
      );
    },
    totalCobrancaExibicao() {
      const totalBackend = this.pickNumber(this.totaisPreviewCobranca, [
        "valor_total",
        "total",
        "total_cobranca",
      ]);

      if (this.possuiAjusteLocal || this.valorAcrescimoCobranca > 0) {
        return this.totalCobrancaLocal;
      }

      return this.roundCurrency(totalBackend ?? this.totalCobrancaLocal);
    },
    totaisCobrancaCalculados() {
      return {
        valor_produtos: this.subtotalProdutosCobranca,
        subtotal_produtos: this.subtotalProdutosCobranca,
        valor_subtotal: this.subtotalProdutosCobranca,
        taxa_servico_percentual: this.taxaServicoPercentualAtual,
        valor_taxa_servico: this.valorTaxaServico,
        taxa_servico_valor: this.valorTaxaServico,
        couvert_valor: this.couvertValorAtual,
        valor_couvert: this.valorCouvertTotal,
        couvert_total: this.valorCouvertTotal,
        valor_acrescimo: this.valorAcrescimoCobranca,
        acrescimo: this.valorAcrescimoCobranca,
        extra_value: this.valorAcrescimoCobranca,
        extraValue: this.valorAcrescimoCobranca,
        tipoAcrescimo: this.tipoAcrescimoCobranca,
        tipoAcrescimoValue: this.valorAcrescimoCobranca > 0 ? 1 : null,
        valor_total: this.totalCobrancaExibicao,
        total: this.totalCobrancaExibicao,
        total_cobranca: this.totalCobrancaExibicao,
      };
    },
    tipoAcrescimoCobranca() {
      return this.valorAcrescimoCobranca > 0
        ? { value: 1, text: "Valor R$" }
        : null;
    },
    checkoutSteps() {
      return [
        {
          label: "Selecionar",
          active: this.cobrancaItens.length > 0,
          done: this.cobrancaItens.length > 0,
        },
        {
          label: "Revisar",
          active: Boolean(this.preview),
          done: Boolean(this.preview || this.rascunhoCobrancaAtual),
        },
        {
          label: "Preparar",
          active: Boolean(this.rascunhoCobrancaAtual),
          done: Boolean(this.rascunhoCobrancaAtual || this.preVendaGerada),
        },
        {
          label: "Pagar",
          active: Boolean(
            this.preVendaGerada || this.nfeGerada || this.pagamentoFechamento
          ),
          done: false,
        },
      ];
    },
    canPreview() {
      return (
        !this.isBlocked &&
        !this.previewing &&
        this.cobrancaItens.length > 0 &&
        (this.form.escopo === "mesa" || Boolean(this.form.comanda_id))
      );
    },
    canCriarRascunho() {
      return (
        this.canPreview &&
        !this.criandoRascunho &&
        !this.rascunhoCobrancaAtual &&
        !this.preVendaGerada
      );
    },
    canGerarPreVenda() {
      return (
        Boolean(this.rascunhoCobrancaAtual?.id) &&
        !this.gerandoPreVenda &&
        !this.preVendaGerada
      );
    },
    canFinalizarPreVenda() {
      return (
        (this.canPreview || Boolean(this.rascunhoCobrancaAtual?.id)) &&
        !this.criandoRascunho &&
        !this.gerandoPreVenda &&
        !this.confirmandoPagamento &&
        !this.preVendaGerada
      );
    },
    canFinalizarNfce() {
      return (
        this.featureFlags.nfceCheckout &&
        (this.canPreview || Boolean(this.rascunhoCobrancaAtual?.id)) &&
        !this.criandoRascunho &&
        !this.gerandoNfce &&
        !this.confirmandoPagamento &&
        !this.preVendaGerada
      );
    },
    totalPago() {
      return this.pagamentos.reduce(
        (total, pagamento) => total + Number(pagamento.valor || 0),
        0
      );
    },
    restantePagamento() {
      return Math.max(
        Number(this.pagamentoFechamento?.valor_total || 0) - this.totalPago,
        0
      );
    },
    trocoPagamento() {
      return Math.max(
        this.totalPago - Number(this.pagamentoFechamento?.valor_total || 0),
        0
      );
    },
    pagamentoFechamentoValor() {
      return Number(this.pagamentoFechamento?.valor_total || 0);
    },
    pagamentoFechamentoItens() {
      return this.pagamentoFechamento?.itens?.length || 0;
    },
    canAdicionarPagamento() {
      return (
        Boolean(this.pagamentoFechamento?.id) &&
        Boolean(this.pagamentoForm.forma_pagamento_id) &&
        Number(this.pagamentoForm.valor || 0) > 0
      );
    },
    canConfirmarPagamento() {
      return (
        Boolean(this.pagamentoFechamento?.id) &&
        this.pagamentos.length > 0 &&
        this.restantePagamento <= 0 &&
        !this.confirmandoPagamento &&
        !this.gerandoNfce
      );
    },
    pagamentoModalTitle() {
      if (this.dividindoCobranca) {
        return `Pagamento da pessoa ${this.dividindoPessoaAtual} de ${this.dividindoTotalPessoas}`;
      }

      return this.tipoPagamentoCobranca === "nfce"
        ? "Defina a forma de pagamento da NFC-e"
        : "Defina a forma de pagamento da pré-venda";
    },
    confirmarPagamentoLabel() {
      if (this.confirmandoPagamento || this.gerandoNfce) {
        return this.tipoPagamentoCobranca === "nfce"
          ? "Emitindo NFC-e..."
          : "Finalizando...";
      }

      return this.tipoPagamentoCobranca === "nfce"
        ? "(F5) Emitir NFC-e"
        : "(F8) Finalizar";
    },
    atalhoFinalizacaoPagamento() {
      return this.tipoPagamentoCobranca === "nfce" ? "f5" : "f8";
    },
    ajusteItemModalLabel() {
      const operacao =
        this.ajusteItemModal.operacao === "acrescimo"
          ? "Acréscimo"
          : "Desconto";

      return this.ajusteItemModal.tipo === 2
        ? `${operacao} (%)`
        : `${operacao} (R$)`;
    },
    precoUnitarioComAjusteItem() {
      const precoBase = Number(this.ajusteItemModal.precoAtual || 0);
      const valorAjuste = this.calcularValorAjusteItem();

      if (!Number.isFinite(precoBase) || precoBase <= 0) {
        return 0;
      }

      if (!Number.isFinite(valorAjuste) || valorAjuste <= 0) {
        return precoBase;
      }

      const novoPreco =
        this.ajusteItemModal.operacao === "acrescimo"
          ? precoBase + valorAjuste
          : precoBase - valorAjuste;

      return Math.max(Number(novoPreco.toFixed(2)), 0);
    },
    isBlocked() {
      if (!this.checkout) {
        return true;
      }

      return (
        this.checkout.flags?.atendimento_sem_itens ||
        !this.checkout.flags?.atendimento_aberto ||
        (this.cobrancaItens.length === 0 &&
          Number(this.currentTotals.valor_pendente || 0) <= 0)
      );
    },
    gerarPreVendaLabel() {
      if (this.criandoRascunho) {
        return "Preparando cobrança...";
      }

      if (this.gerandoPreVenda) {
        return "Gerando pré-venda...";
      }

      if (this.preVendaGerada) {
        return "Pré-venda gerada";
      }

      if (!this.rascunhoCobrancaAtual?.id) {
        return "(F8) Gerar pré-venda";
      }

      return "(F8) Gerar pré-venda";
    },
    gerarNfceLabel() {
      if (this.criandoRascunho) {
        return "Preparando cobrança...";
      }

      if (this.gerandoNfce) {
        return "Emitindo NFC-e...";
      }

      return "(F5) Emitir NFC-e";
    },
  },
  mounted() {
    if (this.itsblocked) {
      this.$router.push({ name: "assinatura" });
      return;
    }

    this.getCheckout();
    this.getFormasPagamento();
    this.getConfiguracaoCaixa();
    this.getConfiguracaoFiscalNfce();
    window.addEventListener("keydown", this.handleCheckoutKeydown, {
      capture: true,
    });
    this.fullscreenHandler = () => {
      this.isFullscreen =
        document.fullscreenElement === document.documentElement;
    };
    document.addEventListener("fullscreenchange", this.fullscreenHandler);
    this.fullscreenHandler();
  },
  beforeDestroy() {
    window.removeEventListener("keydown", this.handleCheckoutKeydown, {
      capture: true,
    });
    if (this.fullscreenHandler) {
      document.removeEventListener("fullscreenchange", this.fullscreenHandler);
    }
  },
  beforeRouteLeave(to, from, next) {
    if (this.dividindoCobranca) {
      this.$toast.warning(
        "Finalize a divisão e os pagamentos antes de sair do checkout."
      );
      return next(false);
    }

    // Avisa sobre cobranca em edicao antes de sair (evita perder itens
    // adicionados ao dividir a conta).
    if (
      !this.confirmandoPagamento &&
      this.cobrancaItens.length > 0 &&
      !window.confirm(
        "Existem itens na cobrança que ainda não foram finalizados. Sair mesmo assim?"
      )
    ) {
      return next(false);
    }
    next();
  },
  methods: {
    ...mapActions(["setLoading"]),
    async toggleFullscreen() {
      try {
        if (document.fullscreenElement === document.documentElement) {
          await document.exitFullscreen();
          return;
        }

        if (!document.documentElement?.requestFullscreen) {
          this.$toast.warning(
            "A tela cheia não está disponível neste navegador."
          );
          return;
        }

        await document.documentElement.requestFullscreen();
      } catch (error) {
        this.$toast.warning("Não foi possível abrir o checkout em tela cheia.");
      }
    },
    async getCheckout() {
      try {
        this.setLoading(true);
        const pagadorAtual = {
          escopo: this.form.escopo,
          comanda_id: this.form.comanda_id,
        };
        const payload = this.codigoComandaRoute
          ? { codigo_comanda: this.codigoComandaRoute }
          : { atendimento_id: this.atendimentoId };
        const { data } = await this.$http.post(
          "/restaurante/checkout/find",
          payload
        );
        this.checkout = data;
        this.preview = null;

        const comandaIdRoute = this.checkoutComandaIdRoute;

        if (comandaIdRoute) {
          this.form.escopo = "comanda";
          this.form.comanda_id = Number(comandaIdRoute);
        } else if (data.checkout_comanda_id) {
          this.form.escopo = "comanda";
          this.form.comanda_id = data.checkout_comanda_id;
        } else if (this.pagadorComandaAindaExiste(pagadorAtual, data)) {
          this.form.escopo = "comanda";
          this.form.comanda_id = pagadorAtual.comanda_id;
        } else {
          this.form.escopo = "mesa";
          this.form.comanda_id = null;
        }

        this.syncPagadorFromForm();

        if (!this.cobrancaItens.length) {
          this.carregarItensDoPagador(false);
        }

        if (data.fechamento_convertivel && !this.preVendaGerada) {
          this.rascunho = this.normalizarFechamentoComAjustes(
            data.fechamento_convertivel
          );
        }
      } catch (error) {
        this.handleError(error, "Não foi possível carregar o checkout.");
      } finally {
        this.setLoading(false);
      }
    },
    pagadorComandaAindaExiste(pagador, checkout) {
      if (pagador.escopo !== "comanda" || !pagador.comanda_id) {
        return false;
      }

      return (checkout?.comandas || []).some(
        (comanda) => Number(comanda.id) === Number(pagador.comanda_id)
      );
    },
    async getFormasPagamento() {
      try {
        const { data } = await this.$http.post("/financeiro/formas-pagamento");
        this.formasPagamento = data || [];

        const padrao =
          this.formasPagamento.find((forma) => forma.default) ||
          this.formasPagamento[0];

        if (padrao && !this.pagamentoForm.forma_pagamento_id) {
          this.pagamentoForm.forma_pagamento_id = padrao.id;
        }
      } catch (error) {
        this.handleError(
          error,
          "Não foi possível carregar formas de pagamento."
        );
      }
    },
    async getConfiguracaoCaixa() {
      try {
        const { data } = await this.$http.post("/configuracoesgerais/find");
        const configuracao = Array.isArray(data) ? data[0] : data;
        this.aberturaCaixaObrigatoria = Boolean(
          Number(configuracao?.abertura_caixa || 0)
        );
        this.restauranteImpressaoAtiva = Boolean(
          Number(configuracao?.restaurante_impressao_qz_tray || 0)
        );
        this.restauranteImpressoraCheckout =
          configuracao?.restaurante_impressora_checkout || null;
        this.restauranteImpressoraComprovante =
          configuracao?.restaurante_impressora_comprovante_cliente ||
          configuracao?.restaurante_impressora_checkout ||
          null;
      } catch (error) {
        this.aberturaCaixaObrigatoria = false;
        this.restauranteImpressaoAtiva = false;
        this.restauranteImpressoraCheckout = null;
        this.restauranteImpressoraComprovante = null;
      }
    },
    async getConfiguracaoFiscalNfce() {
      try {
        const { data } = await this.$http.post(
          "/configuracoes/current-config",
          {
            modelo: 65,
          }
        );
        const configuracao = Array.isArray(data) ? data[0] : data;

        this.serieNfce = configuracao?.serie || this.serieNfce;
        this.informacoesComplementaresNfce =
          configuracao?.informacoesComplementares || "";
      } catch (error) {
        this.serieNfce = this.serieNfce || 99;
        this.informacoesComplementaresNfce = "";
      }
    },
    handleCheckoutKeydown(event) {
      const key = String(event.key || "").toLowerCase();

      if (event.repeat) {
        return;
      }

      if (this.isModalVisible("modal-pagamento-restaurante")) {
        const atalhoPagamento = this.atalhosPagamento.find(
          (atalho) => atalho.tecla.toLowerCase() === key
        );

        if (atalhoPagamento) {
          event.preventDefault();
          event.stopPropagation();
          this.adicionarPagamentoPorAtalho(atalhoPagamento);
          return;
        }

        if (key === this.atalhoFinalizacaoPagamento) {
          event.preventDefault();
          event.stopPropagation();
          this.confirmarPagamento();
        }

        return;
      }

      if (
        this.isModalVisible("modal-abertura-caixa-restaurante") ||
        this.isModalVisible("modal-ajuste-item-restaurante") ||
        this.isModalVisible("modal-vincular-avulsa-checkout")
      ) {
        return;
      }

      if (key === "f8") {
        event.preventDefault();
        event.stopPropagation();
        this.finalizarCobranca("pre_venda");
        return;
      }

      if (key === "f5" && this.featureFlags.nfceCheckout) {
        event.preventDefault();
        event.stopPropagation();
        this.finalizarCobranca("nfce");
      }
    },
    isModalVisible(id) {
      const modal = document.getElementById(id);
      return (
        !!modal &&
        modal.classList.contains("show") &&
        modal.getAttribute("aria-hidden") !== "true"
      );
    },
    async ensureCaixaAbertoParaPdv(tipo = "pre_venda") {
      if (this.aberturaCaixaObrigatoria === null) {
        await this.getConfiguracaoCaixa();
      }

      if (!this.aberturaCaixaObrigatoria) {
        return true;
      }

      try {
        const { data } = await this.$http.post("/caixa/get-caixa");

        if (data?.caixa_aberto) {
          return true;
        }

        this.gerarPreVendaPendenteAbertura = tipo === "pre_venda";
        this.tipoCobrancaPendenteAbertura = tipo;
        this.$bvModal.show("modal-abertura-caixa-restaurante");
        return false;
      } catch (error) {
        this.handleError(error, "Não foi possível verificar o caixa.");
        return false;
      }
    },
    focusValorAberturaCaixa() {
      this.$nextTick(() => {
        const inputEl =
          this.$refs.caixaValorAberturaRestaurante?.$el ||
          this.$refs.caixaValorAberturaRestaurante;

        if (inputEl) {
          inputEl.focus();
          inputEl.select();
        }
      });
    },
    cancelarAberturaCaixa(cancel) {
      this.gerarPreVendaPendenteAbertura = false;
      this.tipoCobrancaPendenteAbertura = null;
      cancel();
    },
    async abrirCaixaCheckout() {
      try {
        this.abrindoCaixa = true;
        this.setLoading(true);
        const { data } = await this.$http.post("/caixa/create", {
          id_tipo: 1,
          impressao_comprovante: this.caixaImpressaoComprovante,
          valor_abertura: Number(this.caixaValorAbertura || 0),
        });

        if (this.caixaImpressaoComprovante && data?.["comprovante-url"]) {
          await this.imprimirDocumentoPdv(data["comprovante-url"]);
        }

        this.$toast.success("Caixa aberto com sucesso.");
        const tipoPendente =
          this.tipoCobrancaPendenteAbertura ||
          (this.gerarPreVendaPendenteAbertura ? "pre_venda" : null);
        this.gerarPreVendaPendenteAbertura = false;
        this.tipoCobrancaPendenteAbertura = null;
        this.$bvModal.hide("modal-abertura-caixa-restaurante");

        if (tipoPendente) {
          await this.finalizarCobranca(tipoPendente);
        }
      } catch (error) {
        this.handleError(error, "Não foi possível abrir o caixa.");
      } finally {
        this.abrindoCaixa = false;
        this.setLoading(false);
      }
    },
    syncPagadorFromForm() {
      this.form.pagador =
        this.form.escopo === "mesa"
          ? "mesa"
          : `comanda:${this.form.comanda_id}`;
    },
    openVincularAvulsaModal() {
      this.avulsaMesaForm.codigo = "";
      this.$bvModal.show("modal-vincular-avulsa-checkout");
    },
    focusCodigoAvulsaMesa() {
      this.$nextTick(() => {
        if (this.$refs.codigoAvulsaMesa) {
          this.$refs.codigoAvulsaMesa.focus();
        }
      });
    },
    async vincularAvulsaMesa() {
      const codigo = String(this.avulsaMesaForm.codigo || "").trim();

      if (!codigo) {
        this.$toast.warning("Informe o código da comanda avulsa.");
        return;
      }

      if (!this.checkout?.mesa?.id) {
        this.$toast.warning("Checkout sem mesa para vincular comanda.");
        return;
      }

      try {
        this.vinculandoAvulsa = true;
        const { data } = await this.$http.post(
          "/restaurante/comandas/avulsas/find-or-create",
          { codigo }
        );
        await this.$http.post("/restaurante/comandas/avulsas/vincular-mesa", {
          comanda_id: data?.comanda?.id,
          mesa_id: this.checkout.mesa.id,
        });
        this.$toast.success("Comanda avulsa adicionada à mesa.");
        this.$bvModal.hide("modal-vincular-avulsa-checkout");
        await this.getCheckout();
        this.selectMesaInteira();
      } catch (error) {
        this.handleError(error, "Não foi possível adicionar a comanda avulsa.");
      } finally {
        this.vinculandoAvulsa = false;
      }
    },
    selectMesaInteira() {
      if (this.modoApenasComandasCheckout) {
        return;
      }

      this.form.escopo = "mesa";
      this.form.comanda_id = null;
      this.form.pagador = "mesa";
      this.carregarSaldoMesa();
    },
    selectComanda(comandaId) {
      this.form.escopo = "comanda";
      this.form.comanda_id = comandaId;
      this.form.pagador = `comanda:${comandaId}`;
      this.carregarItensDaComanda(comandaId);
    },
    selectPagador(value) {
      if (value === "mesa") {
        if (this.modoApenasComandasCheckout) {
          return;
        }

        this.selectMesaInteira();
        return;
      }

      const comandaId = Number(String(value || "").replace("comanda:", ""));
      if (!Number.isFinite(comandaId) || comandaId <= 0) {
        this.selectMesaInteira();
        return;
      }

      this.selectComanda(comandaId);
    },
    carregarItensDoPagador(showEmptyMessage = true) {
      if (this.form.escopo === "mesa") {
        this.carregarSaldoMesa(showEmptyMessage);
        return;
      }

      this.carregarItensDaComanda(this.form.comanda_id, showEmptyMessage);
    },
    carregarSaldoMesa(showEmptyMessage = true) {
      this.cobrancaItens = this.itensPendentes.map((item) =>
        this.buildLinhaCobranca(item)
      );

      if (showEmptyMessage && !this.cobrancaItens.length) {
        this.$toast.warning("A mesa não possui itens pendentes.");
      }

      this.resetEstadoCobranca();
    },
    carregarItensDaComanda(comandaId, showEmptyMessage = true) {
      if (!comandaId) {
        this.cobrancaItens = [];
        this.resetEstadoCobranca();
        return;
      }

      this.cobrancaItens = this.itensPendentes
        .filter((item) => Number(item.comanda_id || 0) === Number(comandaId))
        .map((item) => this.buildLinhaCobranca(item));

      if (showEmptyMessage && !this.cobrancaItens.length) {
        this.$toast.warning("Essa comanda não possui itens pendentes.");
      }

      this.resetEstadoCobranca();
    },
    buildLinhaCobranca(item) {
      return {
        atendimento_item_id: item.id,
        quantidade: Number(item.quantidade_pendente || 0),
        ajuste_operacao: null,
        ajuste_tipo: 1,
        ajuste_valor: 0,
      };
    },
    resetEstadoCobranca() {
      this.preview = null;
      this.rascunho = null;
      this.preVendaGerada = null;
      this.nfeGerada = null;
      this.cobrancaIdempotencyToken = null;
    },
    addItemCobranca(item) {
      if (!this.canAddItem(item)) {
        return;
      }

      const existing = this.cobrancaItens.find(
        (linha) => linha.atendimento_item_id === item.id
      );

      if (existing) {
        this.$toast.warning("Item já está na cobrança.");
        return;
      }

      this.cobrancaItens.push(this.buildLinhaCobranca(item));
      this.resetEstadoCobranca();
    },
    adicionarSaldoTotal() {
      this.selectMesaInteira();
    },
    updateQuantidade(linha) {
      const target = this.cobrancaItens.find(
        (item) => item.atendimento_item_id === linha.atendimento_item_id
      );

      if (!target) {
        return;
      }

      target.quantidade = Number(linha.quantidade || 0);
      this.resetEstadoCobranca();
    },
    removeItemCobranca(itemId) {
      this.cobrancaItens = this.cobrancaItens.filter(
        (item) => item.atendimento_item_id !== itemId
      );
      this.resetEstadoCobranca();
    },
    resetCobranca() {
      this.resetEstadoCobranca();
      this.carregarItensDoPagador(false);
    },
    canAddItem(item) {
      return (
        !this.dividindoCobranca &&
        item.status !== "cancelado" &&
        item.cobravel !== false &&
        Number(item.quantidade_pendente || 0) > 0 &&
        Number(item.valor_pendente || 0) > 0 &&
        !this.cobrancaItens.some(
          (linha) => linha.atendimento_item_id === item.id
        )
      );
    },
    itemActionLabel(item) {
      if (item.cobravel === false) {
        return "Sem valor";
      }

      const status = this.statusCobrancaItemLabel(item);

      if (status === "Cobrado") {
        return "Pago";
      }

      if (
        this.cobrancaItens.some(
          (linha) => linha.atendimento_item_id === item.id
        )
      ) {
        return "Adicionado";
      }

      if (status === "Reservado") {
        return "Reservado";
      }

      return "Adicionar";
    },
    requestPayload() {
      // O backend recalcula todos os totais (subtotal, taxa, couvert, acrescimo,
      // total) a partir dos itens e da configuracao. O front envia apenas os
      // insumos; nunca confia/envia valor_total/price/extra_value do cliente.
      return {
        atendimento_id: this.checkout?.atendimento?.id || this.atendimentoId,
        tipo: "total",
        escopo: this.form.escopo,
        comanda_id:
          this.form.escopo === "comanda" ? this.form.comanda_id : null,
        itens: this.cobrancaItens.map((item) => ({
          atendimento_item_id: item.atendimento_item_id,
          quantidade: item.quantidade,
          ajuste_operacao: item.ajuste_operacao,
          ajuste_tipo: item.ajuste_tipo,
          ajuste_valor: item.ajuste_valor,
        })),
        taxa_servico_percentual: Number(this.taxaServicoPercentual || 0),
        couvert_valor: Number(this.couvertValor || 0),
      };
    },
    normalizarPreviewCobranca(data) {
      return {
        ...(data || {}),
        totais_cobranca: {
          ...(data?.totais_cobranca || {}),
          ...this.totaisCobrancaCalculados,
        },
      };
    },
    deveAplicarAjustesAoFechamento(fechamento) {
      return (
        this.cobrancaItensDetalhados.length > 0 ||
        Boolean(this.preview) ||
        (this.rascunhoCobrancaAtual?.id &&
          Number(fechamento?.id || 0) ===
            Number(this.rascunhoCobrancaAtual.id || 0))
      );
    },
    normalizarFechamentoComAjustes(fechamento) {
      if (!fechamento || !this.deveAplicarAjustesAoFechamento(fechamento)) {
        return fechamento;
      }

      const totais = {
        ...(fechamento.totais_cobranca || {}),
        ...this.totaisCobrancaCalculados,
      };
      const valorTotal = this.roundCurrency(
        totais.valor_total || this.totalCobrancaExibicao
      );

      return {
        ...fechamento,
        valor_total: valorTotal,
        valor_acrescimo: this.valorAcrescimoCobranca,
        extra_value: this.valorAcrescimoCobranca,
        totais_cobranca: {
          ...totais,
          valor_total: valorTotal,
          total: valorTotal,
          total_cobranca: valorTotal,
        },
      };
    },
    valorFechamentoExibicao(fechamento) {
      if (
        this.rascunhoCobrancaAtual?.id &&
        Number(fechamento?.id || 0) ===
          Number(this.rascunhoCobrancaAtual.id || 0)
      ) {
        return this.totalCobrancaExibicao;
      }

      return (
        this.pickNumber(fechamento?.totais_cobranca, [
          "valor_total",
          "total",
          "total_cobranca",
        ]) ?? Number(fechamento?.valor_total || 0)
      );
    },
    async previewCheckout() {
      try {
        this.previewing = true;
        this.setLoading(true);
        const { data } = await this.$http.post(
          "/restaurante/checkout/preview",
          this.requestPayload()
        );
        this.preview = this.normalizarPreviewCobranca(data);
        this.$toast.success("Cobrança simulada.");
      } catch (error) {
        this.applyBackendData(error);
        this.handleError(error, "Não foi possível simular a cobrança.");
      } finally {
        this.previewing = false;
        this.setLoading(false);
      }
    },
    async criarRascunho() {
      try {
        this.criandoRascunho = true;
        this.setLoading(true);
        const { data } = await this.$http.post(
          "/restaurante/checkout/criar-rascunho",
          {
            ...this.requestPayload(),
            idempotency_key: this.idempotencyKey(),
          }
        );
        this.rascunho = this.normalizarFechamentoComAjustes(data);
        this.$toast.success("Rascunho criado.");
        await this.getCheckout();
        return data;
      } catch (error) {
        this.applyBackendData(error);
        this.handleError(error, "Não foi possível criar o rascunho.");
        return null;
      } finally {
        this.criandoRascunho = false;
        this.setLoading(false);
      }
    },
    async confirmarDividirCobranca() {
      if (!this.podeDividirCobranca) {
        return;
      }

      const n = Math.floor(Number(this.dividirPessoas || 0));
      const confirmou = await this.$swal.fire({
        icon: "question",
        title: "Preparar divisão",
        html: `Os itens selecionados serão separados igualmente para <strong>${n} pessoas</strong>. Nenhuma pré-venda, NFC-e ou cobrança será gerada agora. Deseja preparar a primeira pessoa?`,
        showCancelButton: true,
        confirmButtonText: "Preparar divisão",
        cancelButtonText: "Cancelar",
        confirmButtonColor: "#3699ff",
      });

      if (!confirmou.isConfirmed) {
        return;
      }

      await this.executarDividirCobranca(n);
    },
    executarDividirCobranca(n) {
      const cobrancaItensOriginal = this.cobrancaItens.map((item) => ({
        ...item,
      }));
      const itensDivisao = cobrancaItensOriginal.map((item) => ({
        id: item.atendimento_item_id,
        quantidade_pendente: Number(item.quantidade || 0),
        ajuste_operacao: item.ajuste_operacao,
        ajuste_tipo: item.ajuste_tipo,
        ajuste_valor: item.ajuste_valor,
      }));
      const partes = Array.from({ length: n }, (_, index) =>
        this.montarItensDivididos(n, index + 1, itensDivisao)
      );
      const pessoaSemItens = partes.findIndex((itens) => !itens.length);

      if (pessoaSemItens >= 0) {
        this.$toast.warning(
          `Não é possível dividir estes itens entre ${n} pessoas. Reduza a quantidade de pessoas.`
        );
        return;
      }

      this.dividindoCobranca = true;
      this.dividindoTotalPessoas = n;
      this.divisaoPartes = partes;
      this.divisaoToken = this.criarTokenIdempotencia("divisao");
      this.divisaoTaxaServicoOriginal = Number(this.taxaServicoPercentual || 0);
      this.divisaoCouvertOriginal = Number(this.couvertValor || 0);
      this.divisaoCobrancaItensOriginal = cobrancaItensOriginal;
      this.prepararPessoaDivisao(1);
    },
    prepararPessoaDivisao(indicePessoa) {
      const itensPessoa = this.divisaoPartes[indicePessoa - 1] || [];

      if (!itensPessoa.length) {
        this.$toast.error(
          `Não foi possível preparar a cobrança da pessoa ${indicePessoa}.`
        );
        return;
      }

      this.dividindoPessoaAtual = indicePessoa;
      this.taxaServicoPercentual = this.divisaoTaxaServicoOriginal;
      this.couvertValor = this.dividirValor(
        this.divisaoCouvertOriginal,
        this.dividindoTotalPessoas,
        indicePessoa,
        2
      );
      this.cobrancaItens = itensPessoa.map((item) => ({ ...item }));
      this.resetEstadoCobranca();
      this.cobrancaIdempotencyToken = `${this.divisaoToken}-p${indicePessoa}`;
      this.$toast.info(
        `Pessoa ${indicePessoa} de ${this.dividindoTotalPessoas} preparada. Revise os valores e escolha como finalizar.`
      );
    },
    async cancelarDivisaoCobranca() {
      if (!this.dividindoCobranca) {
        return;
      }

      if (this.rascunho?.id || this.preVendaGerada?.fechamento?.id) {
        this.$toast.warning(
          "Cancele primeiro o rascunho ou conclua o documento desta pessoa."
        );
        return;
      }

      const confirmou = await this.$swal.fire({
        icon: "warning",
        title: "Cancelar divisão?",
        text: "A seleção original de itens será restaurada.",
        showCancelButton: true,
        confirmButtonText: "Cancelar divisão",
        cancelButtonText: "Continuar dividindo",
        confirmButtonColor: "#f64e60",
      });

      if (!confirmou.isConfirmed) {
        return;
      }

      this.encerrarDivisaoCobranca({ restaurarSelecao: true });
      this.$toast.info("Divisão cancelada.");
    },
    encerrarDivisaoCobranca({ restaurarSelecao = false } = {}) {
      const itensOriginais = this.divisaoCobrancaItensOriginal.map((item) => ({
        ...item,
      }));
      const taxaOriginal = this.divisaoTaxaServicoOriginal;
      const couvertOriginal = this.divisaoCouvertOriginal;

      this.dividindoCobranca = false;
      this.dividindoPessoaAtual = 0;
      this.dividindoTotalPessoas = 0;
      this.divisaoPartes = [];
      this.divisaoToken = null;
      this.divisaoTaxaServicoOriginal = 0;
      this.divisaoCouvertOriginal = 0;
      this.divisaoCobrancaItensOriginal = [];
      this.cobrancaIdempotencyToken = null;
      this.taxaServicoPercentual = taxaOriginal;
      this.couvertValor = couvertOriginal;

      if (restaurarSelecao) {
        this.cobrancaItens = itensOriginais;
        this.resetEstadoCobranca();
      }
    },
    async avancarDivisaoAposPagamento(liberouMesa) {
      const pessoaConcluida = this.dividindoPessoaAtual;
      const totalPessoas = this.dividindoTotalPessoas;

      if (liberouMesa || pessoaConcluida >= totalPessoas) {
        this.encerrarDivisaoCobranca();
        this.$toast.success(
          `${totalPessoas} cobranças da divisão foram concluídas.`
        );

        if (liberouMesa) {
          this.voltarMesas();
        }
        return;
      }

      this.prepararPessoaDivisao(pessoaConcluida + 1);
    },
    montarItensDivididos(n, indicePessoa, itensBase = this.itensPendentes) {
      // Cada item pendente é dividido igualmente: a pessoa 'indicePessoa'
      // recebe 1/n da quantidade de cada item. A última pessoa absorve o
      // restante para evitar perda por arredondamento.
      const itens = [];
      itensBase.forEach((item) => {
        const quantidadeTotal = Number(item.quantidade_pendente || 0);
        if (quantidadeTotal <= 0) {
          return;
        }

        const quantidadePessoa = this.dividirValor(
          quantidadeTotal,
          n,
          indicePessoa,
          4
        );

        if (quantidadePessoa <= 0) {
          return;
        }

        itens.push({
          atendimento_item_id: item.id,
          quantidade: quantidadePessoa,
          ajuste_operacao: item.ajuste_operacao || null,
          ajuste_tipo: item.ajuste_tipo || null,
          ajuste_valor: Number(item.ajuste_valor || 0),
        });
      });
      return itens;
    },
    dividirValor(valor, totalPartes, indiceParte, casasDecimais) {
      const fator = 10 ** casasDecimais;
      const totalUnidades = Math.round(Number(valor || 0) * fator);
      const baseUnidades = Math.floor(totalUnidades / totalPartes);
      const unidadesParte =
        indiceParte < totalPartes
          ? baseUnidades
          : totalUnidades - baseUnidades * (totalPartes - 1);

      return unidadesParte / fator;
    },
    onPagamentoModalHidden() {
      this.resetPagamentoModal();
    },
    async gerarPreVenda() {
      return this.finalizarCobranca("pre_venda");
    },
    async finalizarCobranca(tipo = "pre_venda") {
      if (!["pre_venda", "nfce"].includes(tipo)) {
        return;
      }

      if (tipo === "nfce" && !this.featureFlags.nfceCheckout) {
        this.$toast.warning("Emissão NFC-e indisponível neste checkout.");
        return;
      }

      const podeFinalizar =
        tipo === "nfce" ? this.canFinalizarNfce : this.canFinalizarPreVenda;

      if (!podeFinalizar) {
        this.$toast.warning("Revise a cobrança antes de finalizar.");
        return;
      }

      const caixaAberto = await this.ensureCaixaAbertoParaPdv(tipo);

      if (!caixaAberto) {
        return;
      }

      const rascunho = await this.ensureRascunhoCobranca();

      if (!rascunho?.id) {
        return;
      }

      if (tipo === "nfce") {
        this.selecionarPagamento(rascunho, "nfce");
        return;
      }

      await this.gerarPreVendaDoRascunho(rascunho);
    },
    async ensureRascunhoCobranca() {
      if (this.dividindoCobranca) {
        if (this.rascunho?.id) {
          return this.rascunho;
        }

        if (!this.canPreview) {
          this.$toast.warning(
            "A cobrança da pessoa atual não possui itens válidos."
          );
          return null;
        }

        return this.criarRascunho();
      }

      if (this.rascunhoAtivo?.id) {
        return this.rascunhoAtivo;
      }

      if (!this.canPreview) {
        this.$toast.warning("Selecione itens para montar a cobrança.");
        return null;
      }

      const rascunho = await this.criarRascunho();
      return rascunho || this.rascunhoAtivo;
    },
    async gerarPreVendaDoRascunho(rascunho, { abrirPagamento = true } = {}) {
      if (!rascunho?.id) {
        this.$toast.warning("Crie um rascunho de cobrança primeiro.");
        return null;
      }

      try {
        this.gerandoPreVenda = true;
        this.setLoading(true);
        const { data } = await this.$http.post(
          `/restaurante/checkout/${rascunho.id}/gerar-pre-venda`,
          this.buildPayloadAjustesCobranca()
        );

        this.preVendaGerada = data;
        this.rascunho = this.normalizarFechamentoComAjustes(data.fechamento);
        if (abrirPagamento) {
          this.selecionarPagamento(this.rascunho, "pre_venda");
        }
        this.cobrancaItens = [];
        this.preview = null;
        this.$toast.success("Pré-venda gerada com sucesso.");
        await this.getCheckout();
        return data;
      } catch (error) {
        if (this.isErroCaixaFechado(error)) {
          this.gerarPreVendaPendenteAbertura = true;
          this.tipoCobrancaPendenteAbertura = "pre_venda";
          this.$bvModal.show("modal-abertura-caixa-restaurante");
          return null;
        }

        this.handleError(error, "Não foi possível gerar a pré-venda.");
        return null;
      } finally {
        this.gerandoPreVenda = false;
        this.setLoading(false);
      }
    },
    isErroCaixaFechado(error) {
      const message = String(
        error?.response?.data?.msg ||
          error?.response?.data?.message ||
          error?.message ||
          ""
      ).toLowerCase();

      return message.includes("caixa") && message.includes("aberto");
    },
    async imprimirPreviewRascunho(fechamento) {
      if (!fechamento?.id) {
        return;
      }

      try {
        this.imprimindoPreview = true;
        const { data } = await this.$http.post(
          `/restaurante/checkout/${fechamento.id}/imprimir-preview`,
          this.buildPayloadAjustesCobranca(fechamento)
        );
        const url = data?.["preview-url"];
        const impressoQz = await this.imprimirPreviewQz(data, fechamento);

        if (!impressoQz && url) {
          window.open(url, "_blank");
        }
      } catch (error) {
        this.handleError(error, "Não foi possível imprimir o preview.");
      } finally {
        this.imprimindoPreview = false;
      }
    },
    selecionarPagamento(fechamento, tipo = "pre_venda") {
      this.tipoPagamentoCobranca = tipo;
      this.pagamentoFechamento = this.normalizarFechamentoPagamento(
        fechamento,
        tipo
      );
      if (
        this.nfeGerada?.fechamento_id !== this.pagamentoFechamento?.id &&
        !this.pagamentoFechamento?.nfe_id
      ) {
        this.nfeGerada = null;
      }
      this.pagamentos = [];
      this.pagamentoForm.valor = Number(
        this.pagamentoFechamento?.valor_total || 0
      );
      this.ensureFormaPagamentoPadrao();
      this.$bvModal.show("modal-pagamento-restaurante");
      this.$nextTick(() => {
        const inputEl =
          this.$refs.valorPagamentoRestaurante?.$el ||
          this.$refs.valorPagamentoRestaurante;

        if (inputEl) {
          inputEl.focus();
          inputEl.select();
        }
      });
    },
    selecionarPagamentoFechamento(fechamento) {
      const tipo = fechamento?.nfe_id ? "nfce" : "pre_venda";

      if (fechamento?.nfe_id) {
        this.nfeGerada = {
          id: fechamento.nfe_id,
          fechamento_id: fechamento.id,
        };
      }

      this.selecionarPagamento(fechamento, tipo);
    },
    canPagarFechamento(fechamento) {
      return (
        !this.dividindoCobranca &&
        fechamento.status === "convertido" &&
        !fechamento.pagamentos?.length &&
        Boolean(
          fechamento.pre_venda_id || fechamento.venda_id || fechamento.nfe_id
        )
      );
    },
    canCancelarRascunho(fechamento) {
      return (
        ["rascunho", "preview", "aguardando_conversao"].includes(
          fechamento.status
        ) &&
        !fechamento.pre_venda_id &&
        !fechamento.venda_id &&
        !fechamento.nfe_id &&
        !fechamento.pagamentos?.length &&
        !this.cancelandoRascunho
      );
    },
    async cancelarRascunho(fechamento) {
      if (!this.canCancelarRascunho(fechamento)) {
        return;
      }

      try {
        this.cancelandoRascunho = true;
        this.setLoading(true);
        await this.$http.post(
          `/restaurante/checkout/${fechamento.id}/cancelar-rascunho`
        );
        this.$toast.success("Rascunho cancelado. Ajuste a cobrança novamente.");
        this.rascunho = null;
        this.preVendaGerada = null;
        this.nfeGerada = null;
        this.cobrancaItens = [];
        await this.getCheckout();
        if (this.dividindoCobranca) {
          this.prepararPessoaDivisao(this.dividindoPessoaAtual);
        }
      } catch (error) {
        this.handleError(error, "Não foi possível cancelar o rascunho.");
      } finally {
        this.cancelandoRascunho = false;
        this.setLoading(false);
      }
    },
    adicionarPagamento() {
      const forma = this.formasPagamento.find(
        (forma) => forma.id === this.pagamentoForm.forma_pagamento_id
      );

      if (!forma) {
        this.$toast.warning("Selecione a forma de pagamento.");
        return;
      }

      this.pagamentos.push({
        forma_pagamento_id: forma.id,
        descricao: forma.descricao,
        valor: Number(this.pagamentoForm.valor || 0),
      });
      this.pagamentoForm.valor = this.restantePagamento;
    },
    adicionarPagamentoPorAtalho(atalho) {
      const forma = this.findFormaPagamentoAtalho(atalho);

      if (!forma) {
        this.$toast.warning(`${atalho.descricao} não encontrado.`);
        return;
      }

      this.pagamentoForm.forma_pagamento_id = forma.id;
      this.pagamentoForm.valor =
        this.restantePagamento || this.pagamentoFechamentoValor;
      this.adicionarPagamento();
    },
    findFormaPagamentoAtalho(atalho) {
      return this.formasPagamento.find((forma) => {
        const descricao = String(forma.descricao || "").toLowerCase();

        return atalho.termos.some((termo) => descricao.includes(termo));
      });
    },
    removerPagamento(index) {
      this.pagamentos.splice(index, 1);
      this.pagamentoForm.valor = this.restantePagamento;
    },
    resetPagamentoModal() {
      if (this.confirmandoPagamento) {
        return;
      }

      this.pagamentos = [];
      this.pagamentoFechamento = null;
      this.pagamentoForm.valor = 0;
      this.tipoPagamentoCobranca = "pre_venda";
    },
    async confirmarPagamento() {
      if (!this.canConfirmarPagamento) {
        return;
      }

      let nfeEmitida = null;

      try {
        this.confirmandoPagamento = true;
        this.setLoading(true);

        if (this.tipoPagamentoCobranca === "nfce") {
          nfeEmitida = this.nfeGerada?.id
            ? this.nfeGerada
            : await this.emitirNfceCheckout();
          this.nfeGerada = nfeEmitida;
        }

        const { data } = await this.$http.post(
          `/restaurante/checkout/${this.pagamentoFechamento.id}/confirmar-pagamento`,
          this.buildPayloadConfirmarPagamento(nfeEmitida)
        );
        const liberouMesa = Boolean(data.liberou_mesa);

        this.$toast.success(
          liberouMesa
            ? "Pagamento confirmado e mesa liberada."
            : "Pagamento confirmado."
        );

        if (this.tipoPagamentoCobranca === "pre_venda") {
          await this.imprimirComprovantePreVenda(data.impressao || data);
        }

        if (data.impressao_erro) {
          this.$toast.warning(
            `Pagamento finalizado, mas não foi possível imprimir: ${data.impressao_erro}`
          );
        }

        this.pagamentoFechamento = null;
        this.pagamentos = [];
        this.rascunho = null;
        this.preVendaGerada = null;
        this.nfeGerada = null;
        this.preview = null;
        this.cobrancaItens = [];

        if (this.dividindoCobranca) {
          if (!liberouMesa) {
            await this.getCheckout();
          }

          this.$bvModal.hide("modal-pagamento-restaurante");
          await this.avancarDivisaoAposPagamento(liberouMesa);
          return;
        }

        if (liberouMesa) {
          this.$bvModal.hide("modal-pagamento-restaurante");
          this.voltarMesas();
          return;
        }

        await this.getCheckout();
        this.$bvModal.hide("modal-pagamento-restaurante");
      } catch (error) {
        if (nfeEmitida?.id) {
          this.handleError(
            error,
            "NFC-e emitida, mas não foi possível vincular o pagamento ao checkout. Atualize a tela e revise a cobrança."
          );
          await this.getCheckout();
          return;
        }

        this.handleError(
          error,
          this.tipoPagamentoCobranca === "nfce"
            ? "Não foi possível emitir a NFC-e."
            : "Não foi possível confirmar o pagamento."
        );
      } finally {
        this.confirmandoPagamento = false;
        this.gerandoNfce = false;
        this.setLoading(false);
      }
    },
    buildPayloadConfirmarPagamento(nfeEmitida = null) {
      return {
        pagamentos: this.pagamentos,
        documento_tipo: this.tipoPagamentoCobranca,
        ...(nfeEmitida?.id && { nfe_id: nfeEmitida.id }),
      };
    },
    buildPayloadAjustesCobranca(fechamento = null) {
      // Mantem apenas insumos + id do fechamento. Totais sao autoridade do
      // backend (recalculados a partir dos itens/configuracao).
      return {
        ...this.requestPayload(),
        fechamento_id: fechamento?.id || this.rascunhoCobrancaAtual?.id || null,
        idempotency_key: this.idempotencyKey(),
      };
    },
    normalizarFechamentoPagamento(fechamento, tipo = "pre_venda") {
      const fechamentoComAjustes =
        this.normalizarFechamentoComAjustes(fechamento) || fechamento || {};
      const valorTotal =
        this.valorFechamentoExibicao(fechamentoComAjustes) ||
        Number(fechamentoComAjustes.valor_total || 0);

      return {
        ...fechamentoComAjustes,
        documento_tipo: tipo,
        valor_total: this.roundCurrency(valorTotal),
        itens:
          fechamentoComAjustes?.itens?.length > 0
            ? fechamentoComAjustes.itens
            : this.cobrancaItensDetalhados,
      };
    },
    async emitirNfceCheckout() {
      this.gerandoNfce = true;
      const payload = this.buildVendaNfcePayload();
      const { data: nota } = await this.$http.post("/nfe/create", {
        dados: payload,
      });
      const notaId = this.getNotaId(nota);

      if (!notaId) {
        throw new Error("NFC-e criada sem identificador retornado.");
      }

      await this.$http.post("/nfe/transmitter", {
        id: notaId,
      });

      const { data: impressao } = await this.$http.post("/nfe/printNota", {
        id: notaId,
      });

      await this.imprimirDocumentosNfce(impressao);

      return {
        id: notaId,
        fechamento_id: this.pagamentoFechamento?.id,
        nota,
        impressao,
      };
    },
    buildVendaNfcePayload() {
      const productOfTradeCollection = this.buildProdutosNfce();
      const paymentModeCollection = this.buildPagamentosNfce();
      const valorProdutos = this.roundCurrency(
        productOfTradeCollection.reduce(
          (total, item) => total + Number(item.totalPrice || 0),
          0
        )
      );
      const extraValue = this.roundCurrency(this.valorAcrescimoCobranca);
      const totalCalculado = this.roundCurrency(valorProdutos + extraValue);
      const price =
        totalCalculado || this.roundCurrency(this.pagamentoFechamentoValor);

      return {
        modelo: 65,
        tpNF: 1,
        serie: this.serieNfce || 99,
        remainder: this.roundCurrency(this.trocoPagamento),
        discount: 0,
        extraValue,
        freightPrice: 0,
        price,
        observation: this.buildObservacaoNfce(),
        buyer: {},
        seller: {},
        paymentModeCollection,
        productOfTradeCollection,
        tipoIntermediador: {
          id: 0,
          name: "Operação sem intermediador (em site ou plataforma própria)",
        },
        intermediador: null,
        usuarioIntermediador: null,
        status: 1,
        isNFce: true,
        tipoAcrescimo: extraValue > 0 ? { value: 1, text: "Valor R$" } : null,
        tipoAcrescimoValue: extraValue > 0 ? 1 : null,
        restaurante_checkout: true,
        restaurante_fechamento_id: this.pagamentoFechamento?.id || null,
      };
    },
    buildProdutosNfce() {
      const linhas = this.linhasFiscaisCobranca();

      if (!linhas.length) {
        throw new Error("Não há itens para emitir a NFC-e.");
      }

      return linhas.map((linha, index) => {
        const quantidade = Number(linha.quantidade || 0);
        const totalPrice = this.roundCurrency(this.valorLinhaFiscal(linha));
        const price =
          quantidade > 0 ? this.roundCurrency(totalPrice / quantidade) : 0;

        return {
          product: this.buildProdutoFiscalCheckout(linha.item, price),
          price,
          quantity: quantidade,
          totalPrice,
          currentIndex: index + 1,
          active: true,
        };
      });
    },
    linhasFiscaisCobranca() {
      if (this.cobrancaItensDetalhados.length) {
        return this.cobrancaItensDetalhados;
      }

      const itens =
        this.pagamentoFechamento?.itens ||
        this.rascunhoCobrancaAtual?.itens ||
        [];

      return itens
        .map((item) => this.normalizarLinhaFechamentoFiscal(item))
        .filter((linha) => linha.item && Number(linha.quantidade || 0) > 0);
    },
    normalizarLinhaFechamentoFiscal(linha) {
      const item =
        linha?.atendimento_item ||
        linha?.item ||
        linha?.restaurante_atendimento_item ||
        linha;

      return {
        ...linha,
        item,
        quantidade: Number(
          linha?.quantidade ||
            linha?.quantidade_cobrada ||
            item?.quantidade_pendente ||
            item?.quantidade ||
            0
        ),
        valor_total: Number(
          linha?.valor_total ||
            linha?.total ||
            linha?.valor ||
            item?.valor_pendente ||
            item?.valor_total ||
            0
        ),
      };
    },
    buildProdutoFiscalCheckout(item, price) {
      const produtoBase = item?.produto || {};
      const produtoId =
        produtoBase.id || produtoBase.id_produto || item?.produto_id || null;
      const produtoUuid =
        produtoBase.uuid || item?.produto_uuid || item?.produto?.uuid || null;

      if (!produtoUuid) {
        throw new Error(
          `Produto fiscal sem UUID: ${this.produtoDescricao(
            item
          )}. Atualize a tela e tente novamente.`
        );
      }

      return {
        ...produtoBase,
        ...(produtoId && { id: produtoId, id_produto: produtoId }),
        uuid: produtoUuid,
        descricao:
          produtoBase.descricao ||
          item?.produto_descricao ||
          this.produtoDescricao(item),
        preco: price,
        originalPreco: produtoBase.originalPreco || produtoBase.preco || price,
      };
    },
    valorLinhaFiscal(linha) {
      if (linha.valor_total !== undefined && Number(linha.valor_total) > 0) {
        return Number(linha.valor_total);
      }

      return this.valorLinhaCobranca(linha);
    },
    buildPagamentosNfce() {
      let restante = this.roundCurrency(this.pagamentoFechamentoValor);
      const pagamentos = this.pagamentos
        .map((pagamento) => {
          const valor = Number(pagamento.valor || 0);
          const valorFiscal = this.roundCurrency(
            Math.min(valor, Math.max(restante, 0))
          );
          restante = this.roundCurrency(restante - valorFiscal);

          return this.normalizarPagamentoNfce(pagamento, valorFiscal);
        })
        .filter((pagamento) => Number(pagamento.value || 0) > 0);

      if (!pagamentos.length) {
        throw new Error(
          "Informe ao menos uma forma de pagamento para a NFC-e."
        );
      }

      return pagamentos;
    },
    normalizarPagamentoNfce(pagamento, valor) {
      const forma =
        this.formasPagamento.find(
          (item) => item.id === pagamento.forma_pagamento_id
        ) || {};
      const descricao = forma.descricao || pagamento.descricao;

      return {
        ...forma,
        id: forma.id || pagamento.forma_pagamento_id,
        descricao,
        id_meio_pagamento: forma.id_meio_pagamento || null,
        vezes: forma.vezes || 1,
        requer_entrada: forma.requer_entrada,
        deleted_at: null,
        created_at: forma.createdAt || forma.created_at || null,
        updated_at: forma.updatedAt || forma.updated_at || null,
        value: valor,
        paymentMode: {
          description: descricao,
          id: forma.id_meio_pagamento || null,
          name: descricao,
        },
        range: [
          {
            value: valor,
            date: this.todayDate(),
          },
        ],
      };
    },
    buildObservacaoNfce() {
      return [
        this.informacoesComplementaresNfce,
        `Checkout restaurante - atendimento #${this.atendimentoId}`,
        this.valorTaxaServico > 0
          ? `Taxa de serviço: ${this.formatMoney(this.valorTaxaServico)}`
          : null,
        this.valorCouvertTotal > 0
          ? `Couvert: ${this.formatMoney(this.valorCouvertTotal)}`
          : null,
      ]
        .filter(Boolean)
        .join("\n");
    },
    async imprimirDocumentosNfce(impressao) {
      if (!impressao) {
        return;
      }

      for (const key of ["danfe-url", "voucher-url", "duplicata"]) {
        if (impressao[key]) {
          await this.imprimirDocumentoPdv(impressao[key]);
        }
      }
    },
    getNotaId(nota) {
      return nota?.id || nota?.nfe?.id || nota?.nota?.id || nota?.data?.id;
    },
    ensureFormaPagamentoPadrao() {
      const atual = this.formasPagamento.find(
        (forma) => forma.id === this.pagamentoForm.forma_pagamento_id
      );

      if (atual) {
        return;
      }

      const padrao =
        this.formasPagamento.find((forma) => {
          const descricao = String(forma.descricao || "").toLowerCase();
          return descricao === "à vista" || descricao === "a vista";
        }) || this.formasPagamento[0];

      this.pagamentoForm.forma_pagamento_id = padrao?.id || null;
    },
    resolveImpressaoQz(data, destinos = []) {
      const origem =
        data?.qz ||
        data?.impressao_qz ||
        data?.impressao?.qz ||
        data?.impressao?.impressao_qz ||
        null;

      const candidatos = [];
      const addCandidato = (item) => {
        if (!item) {
          return;
        }

        if (Array.isArray(item)) {
          item.forEach(addCandidato);
          return;
        }

        if (typeof item === "object") {
          candidatos.push(item);
        }
      };

      addCandidato(data);
      addCandidato(origem);

      if (origem && typeof origem === "object" && !Array.isArray(origem)) {
        destinos.forEach((destino) => addCandidato(origem[destino]));
        ["preview", "checkout", "comprovante", "cliente", "venda"].forEach(
          (destino) => addCandidato(origem[destino])
        );
      }

      return candidatos.find((item) => item?.conteudo) || null;
    },
    async imprimirTextoRestauranteQz(data, destinos, impressoraPadrao) {
      if (!this.restauranteImpressaoAtiva) {
        return false;
      }

      const impressao = this.resolveImpressaoQz(data, destinos);
      const impressora = impressao?.impressora || impressoraPadrao;

      if (!impressora || !impressao?.conteudo) {
        return false;
      }

      try {
        await imprimirTextoQz(impressora, impressao.conteudo);
        this.$toast.success("Preview enviado para a impressora.");
        return true;
      } catch (error) {
        this.$toast.warning(
          error.message ||
            "Não foi possível imprimir o preview no QZ Tray. Abrindo documento."
        );
        return false;
      }
    },
    async imprimirPreviewQz(data, fechamento = null) {
      const conteudoLocal = this.montarConteudoPreviewQz(fechamento, data);

      if (conteudoLocal) {
        return this.imprimirTextoRestauranteQz(
          { conteudo: conteudoLocal },
          ["preview", "checkout"],
          this.restauranteImpressoraCheckout
        );
      }

      return this.imprimirTextoRestauranteQz(
        data,
        ["preview", "checkout"],
        this.restauranteImpressoraCheckout
      );
    },
    async imprimirDocumentoPdv(url) {
      if (!url) {
        return false;
      }

      window.open(url, "_blank");
      return false;
    },
    async imprimirComprovantePreVenda(impressao) {
      if (!impressao) {
        return;
      }

      let comprovanteQzImpresso = false;
      if (
        this.restauranteImpressaoAtiva &&
        this.restauranteImpressoraComprovante &&
        impressao["venda-url"]
      ) {
        try {
          await imprimirPdfQz(
            this.restauranteImpressoraComprovante,
            impressao["venda-url"]
          );
          comprovanteQzImpresso = true;
        } catch (error) {
          this.$toast.warning(
            "Não foi possível imprimir o comprovante pelo QZ Tray. Abrindo o PDF no navegador."
          );
        }
      }

      for (const key of [
        "venda-url",
        "comprovante-url",
        "url_comprovante",
        "url",
        "voucher-url",
        "duplicata",
      ]) {
        if (key === "venda-url" && comprovanteQzImpresso) {
          continue;
        }
        if (impressao[key]) {
          await this.imprimirDocumentoPdv(impressao[key]);
        }
      }
    },
    montarConteudoPreviewQz(fechamento = null, data = null) {
      const itens = this.itensPreviewQz(fechamento, data);

      if (!itens.length) {
        return null;
      }

      const separator = "================================";
      const line = "--------------------------------";
      const mesaNumero =
        this.checkout?.mesa?.numero || this.checkout?.mesa_numero || "-";
      const cobrancaId =
        fechamento?.id || data?.fechamento?.id || data?.id || "-";
      const linhas = [
        separator,
        "PREVIEW DE COBRANCA",
        separator,
        `Atendimento: #${this.atendimentoId || "-"}`,
        `Mesa: ${mesaNumero}`,
        `Cobranca: #${cobrancaId}`,
        `Emitido: ${this.formatDateTimeQz(new Date())}`,
        line,
      ];

      itens.forEach((item) => {
        linhas.push(
          `${this.formatQuantity(item.quantidade)} x ${item.descricao}`
        );
        linhas.push(`  Total: ${this.formatMoney(item.valor_total)}`);
      });

      linhas.push(line);
      linhas.push(`Bruto: ${this.formatMoney(this.subtotalProdutosCobranca)}`);

      if (this.valorTaxaServico > 0) {
        linhas.push(
          `Taxa servico ${this.formatPercentQz(
            this.taxaServicoPercentualAtual
          )}: ${this.formatMoney(this.valorTaxaServico)}`
        );
      }

      if (this.valorCouvertTotal > 0) {
        linhas.push(`Couvert: ${this.formatMoney(this.valorCouvertTotal)}`);
      }

      linhas.push(`Total: ${this.formatMoney(this.totalCobrancaExibicao)}`);
      linhas.push(separator);
      linhas.push("Este documento nao registra");
      linhas.push("pagamento.");
      linhas.push(separator);

      return linhas.join("\n");
    },
    itensPreviewQz(fechamento = null, data = null) {
      if (this.cobrancaItensDetalhados.length) {
        return this.cobrancaItensDetalhados.map((linha) => ({
          descricao: this.truncateQzText(
            this.produtoDescricaoQz(linha.item),
            28
          ),
          quantidade: Number(linha.quantidade || 0),
          valor_total: this.roundCurrency(this.valorLinhaCobranca(linha)),
        }));
      }

      const itens =
        fechamento?.itens || data?.fechamento?.itens || data?.itens || [];

      return itens
        .map((linha) => {
          const normalizada = this.normalizarLinhaFechamentoFiscal(linha);
          const item = normalizada.item || linha;
          const valorTotal =
            this.pickNumber(normalizada, ["valor_total", "total", "valor"]) ||
            this.pickNumber(linha, ["valor_total", "total", "valor"]) ||
            0;

          return {
            descricao: this.truncateQzText(this.produtoDescricaoQz(item), 28),
            quantidade: Number(normalizada.quantidade || 0),
            valor_total: this.roundCurrency(valorTotal),
          };
        })
        .filter((item) => item.quantidade > 0);
    },
    produtoDescricaoQz(item) {
      return (
        item?.produto?.descricao ||
        item?.produto_descricao ||
        item?.descricao ||
        `Produto #${item?.produto_id || item?.id || "-"}`
      );
    },
    truncateQzText(text, maxLength) {
      const value = String(text || "");
      return value.length > maxLength
        ? `${value.slice(0, maxLength - 1)}.`
        : value;
    },
    formatPercentQz(value) {
      return `${this.formatQuantity(value)}%`;
    },
    formatDateTimeQz(date) {
      const pad = (value) => String(value).padStart(2, "0");

      return [
        `${pad(date.getDate())}/${pad(
          date.getMonth() + 1
        )}/${date.getFullYear()}`,
        `${pad(date.getHours())}:${pad(date.getMinutes())}`,
      ].join(" ");
    },
    openAjusteItemModal(linha) {
      const item = linha.item;

      this.ajusteItemModal = {
        atendimento_item_id: linha.atendimento_item_id,
        produto: this.produtoDescricao(item),
        operacao: linha.ajuste_operacao || "desconto",
        tipo: linha.ajuste_tipo || 2,
        valor: linha.ajuste_valor || "",
        precoAtual: this.valorUnitarioBaseCobranca(item),
      };
      this.$bvModal.show("modal-ajuste-item-restaurante");
    },
    setOperacaoAjusteItem(operacao) {
      this.ajusteItemModal.operacao = operacao;
    },
    focusAjusteItemInput() {
      this.$nextTick(() => {
        const inputEl =
          this.$refs.ajusteItemInput?.$el || this.$refs.ajusteItemInput;

        if (inputEl) {
          inputEl.focus();
          inputEl.select();
        }
      });
    },
    parseValorAjusteItem(value) {
      if (typeof value === "number") {
        return value;
      }

      const valueString = String(value || "0")
        .replace("R$", "")
        .replace("%", "")
        .trim();

      if (!valueString) {
        return 0;
      }

      const normalized = valueString.includes(",")
        ? valueString.replace(/\./g, "").replace(",", ".")
        : valueString;

      return Number(normalized);
    },
    calcularValorAjusteItem() {
      const precoBase = Number(this.ajusteItemModal.precoAtual || 0);
      const valorAjuste = this.parseValorAjusteItem(this.ajusteItemModal.valor);

      if (!Number.isFinite(precoBase) || precoBase <= 0) {
        return 0;
      }

      if (!Number.isFinite(valorAjuste) || valorAjuste <= 0) {
        return 0;
      }

      return this.ajusteItemModal.tipo === 2
        ? precoBase * (valorAjuste / 100)
        : valorAjuste;
    },
    aplicarAjusteItem() {
      const valorAjuste = this.parseValorAjusteItem(this.ajusteItemModal.valor);
      const linha = this.cobrancaItens.find(
        (item) =>
          item.atendimento_item_id === this.ajusteItemModal.atendimento_item_id
      );

      if (!linha) {
        return;
      }

      if (!Number.isFinite(valorAjuste) || valorAjuste <= 0) {
        this.$toast.warning("Informe o valor do ajuste.");
        this.focusAjusteItemInput();
        return;
      }

      if (
        this.ajusteItemModal.operacao === "desconto" &&
        this.ajusteItemModal.tipo === 2 &&
        valorAjuste > 100
      ) {
        this.$toast.warning("O desconto percentual não pode passar de 100%.");
        this.focusAjusteItemInput();
        return;
      }

      const valorAjusteEmReais = this.calcularValorAjusteItem();

      if (
        this.ajusteItemModal.operacao === "desconto" &&
        valorAjusteEmReais > Number(this.ajusteItemModal.precoAtual || 0)
      ) {
        this.$toast.warning(
          "O desconto em reais não pode ser maior que o preço unitário."
        );
        this.focusAjusteItemInput();
        return;
      }

      linha.ajuste_operacao = this.ajusteItemModal.operacao;
      linha.ajuste_tipo = this.ajusteItemModal.tipo;
      linha.ajuste_valor = valorAjuste;
      this.resetEstadoCobranca();
      this.$bvModal.hide("modal-ajuste-item-restaurante");
      this.$toast.success("Ajuste aplicado no item.");
    },
    valorUnitarioBaseCobranca(item) {
      const quantidadePendente = Number(item.quantidade_pendente || 0);

      if (quantidadePendente > 0) {
        return Number(item.valor_pendente || 0) / quantidadePendente;
      }

      return Number(item.valor_unitario || 0);
    },
    valorLinhaCobranca(linha) {
      const item = linha.item;
      const quantidade = Number(linha.quantidade || 0);
      const valorUnitario = this.valorUnitarioBaseCobranca(item);
      const valorAjuste = this.calcularValorAjusteLinha(linha, valorUnitario);
      const valorFinal =
        linha.ajuste_operacao === "acrescimo"
          ? valorUnitario + valorAjuste
          : valorUnitario - valorAjuste;

      return Math.max(valorFinal, 0) * quantidade;
    },
    calcularValorAjusteLinha(linha, valorUnitario) {
      const valorAjuste = Number(linha.ajuste_valor || 0);

      if (!linha.ajuste_operacao || valorAjuste <= 0) {
        return 0;
      }

      return Number(linha.ajuste_tipo || 1) === 2
        ? valorUnitario * (valorAjuste / 100)
        : valorAjuste;
    },
    ajusteItemLabel(linha) {
      const operacao =
        linha.ajuste_operacao === "acrescimo" ? "Acréscimo" : "Desconto";
      const valor =
        Number(linha.ajuste_tipo || 1) === 2
          ? `${this.formatQuantity(linha.ajuste_valor)}%`
          : this.formatMoney(linha.ajuste_valor);

      return `${operacao} ${valor}`;
    },
    idempotencyKey() {
      // O token nasce uma vez por tentativa e permanece estável nas
      // retentativas. Ao alterar a cobrança ou concluir um pagamento ele é
      // renovado, permitindo novos fechamentos parciais no mesmo atendimento.
      if (!this.cobrancaIdempotencyToken) {
        this.cobrancaIdempotencyToken = this.criarTokenIdempotencia("cobranca");
      }

      return [
        "restaurante-cobranca",
        this.atendimentoId,
        this.form.escopo,
        this.form.comanda_id || "mesa",
        this.cobrancaIdempotencyToken,
      ]
        .join("-")
        .slice(0, 120);
    },
    criarTokenIdempotencia(prefix) {
      return [
        prefix,
        Date.now().toString(36),
        Math.random().toString(36).slice(2, 10),
      ].join("-");
    },
    applyBackendData(error) {
      const data = error?.response?.data?.data;

      if (data && data.atendimento) {
        this.checkout = data;
      }
    },
    handleError(error, fallback) {
      const message =
        error?.response?.data?.msg ||
        error?.response?.data?.message ||
        error?.message ||
        fallback;
      this.$toast.error(message);
    },
    voltarMesas() {
      this.$router.push({ name: "restauranteCaixa" });
    },
    produtoDescricao(item) {
      return item.produto
        ? item.produto.descricao
        : `Produto #${item.produto_id}`;
    },
    comandaLabel(comanda) {
      if (!comanda) {
        return "Sem comanda";
      }

      if (comanda.tipo === "avulso" || (!comanda.nome && comanda.codigo)) {
        return `Avulsa ${comanda.codigo || comanda.id}`;
      }

      return comanda.nome || `Comanda ${comanda.numero || comanda.id}`;
    },
    itemComandaOrigem(item) {
      return item.comanda ? `Origem: ${this.comandaLabel(item.comanda)}` : "-";
    },
    fechamentoLabel(fechamento) {
      if (fechamento.escopo === "comanda") {
        return fechamento.comanda
          ? this.comandaLabel(fechamento.comanda)
          : "Comanda";
      }

      return "Mesa inteira";
    },
    statusCobrancaItemLabel(item) {
      if (item.cancelado) {
        return "Cancelado";
      }

      if (item.cobravel === false) {
        return "Sem valor";
      }

      const total = Number(item.quantidade_total || item.quantidade || 0);
      const cobrado = Number(item.quantidade_ja_cobrada || 0);
      const reservado = Number(item.quantidade_reservada || 0);
      const pendente = Number(item.quantidade_pendente || 0);

      if (total > 0 && cobrado >= total) {
        return "Cobrado";
      }

      if (reservado > 0 && pendente <= 0) {
        return "Reservado";
      }

      if (cobrado > 0 || reservado > 0) {
        return "Parcial";
      }

      return "Pendente";
    },
    statusCobrancaItemClass(item) {
      const label = this.statusCobrancaItemLabel(item);
      const classes = {
        Cancelado: "label label-lg label-inline label-light-danger",
        Cobrado: "label label-lg label-inline label-light-success",
        Reservado: "label label-lg label-inline label-light-warning",
        Parcial: "label label-lg label-inline label-light-primary",
        Pendente: "label label-lg label-inline label-light-dark",
        "Sem valor": "label label-lg label-inline label-light-info",
      };

      return classes[label] || "label label-lg label-inline label-light";
    },
    formatMoney(value) {
      return Number(value || 0).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      });
    },
    formatQuantity(value) {
      return Number(value || 0).toLocaleString("pt-BR", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 4,
      });
    },
    pickNumber(source, keys) {
      const found = keys
        .map((key) => source?.[key])
        .find((value) => value !== undefined && value !== null && value !== "");

      if (found === undefined) {
        return null;
      }

      const number = Number(found);
      return Number.isFinite(number) ? number : null;
    },
    roundCurrency(value) {
      const number = Number(value || 0);
      return Number.isFinite(number) ? Number(number.toFixed(2)) : 0;
    },
    todayDate() {
      const date = new Date();
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },
  },
};
</script>

<style scoped>
.restaurante-checkout .card-body {
  min-height: 420px;
}

.restaurante-checkout.is-fullscreen {
  background: #eef1f6;
  height: 100vh;
  height: 100dvh;
  overflow-x: hidden;
  overflow-y: auto;
  padding: 8px;
}

.restaurante-checkout.is-fullscreen > .card {
  border: 0;
  border-radius: 8px;
  margin: 0;
  min-height: calc(100vh - 16px);
  min-height: calc(100dvh - 16px);
}

.checkout-layout {
  align-items: flex-start;
}

.checkout-header-block {
  border-bottom: 1px solid #ebedf3;
  margin-bottom: 18px;
  padding-bottom: 16px;
}

.checkout-title {
  align-items: center;
  display: flex;
  gap: 14px;
  justify-content: space-between;
  margin-bottom: 16px;
}

.checkout-fullscreen-button {
  align-items: center;
  background: #f3f6f9;
  border: 1px solid #e4e6ef;
  border-radius: 6px;
  color: #3f4254;
  display: inline-flex;
  flex: 0 0 38px;
  height: 38px;
  justify-content: center;
  transition: background-color 0.15s ease, border-color 0.15s ease;
  width: 38px;
}

.checkout-fullscreen-button:hover,
.checkout-fullscreen-button:focus-visible {
  background: #e8fff3;
  border-color: #1bc5bd;
  color: #0bb783;
}

.checkout-title h3 {
  color: #181c32;
  font-size: 1.35rem;
  font-weight: 700;
  margin: 0 0 4px;
}

.checkout-title span {
  color: #7e8299;
  font-weight: 600;
}

.checkout-flow {
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  margin-top: 16px;
}

.checkout-flow-step {
  align-items: center;
  background: #f3f6f9;
  border: 1px solid #e4e6ef;
  border-radius: 6px;
  color: #7e8299;
  display: flex;
  gap: 8px;
  min-height: 42px;
  padding: 8px 10px;
}

.checkout-flow-step span {
  background: #b5b5c3;
  border-radius: 999px;
  height: 10px;
  width: 10px;
}

.checkout-flow-step strong {
  font-size: 0.86rem;
}

.checkout-flow-step.active {
  background: #eef6ff;
  border-color: #3699ff;
  color: #181c32;
}

.checkout-flow-step.done span {
  background: #1bc5bd;
}

.checkout-actions {
  display: grid;
  gap: 8px;
  grid-template-columns: 1fr auto;
  margin-bottom: 16px;
}

.empty-state,
.blocked-state {
  background: #f3f6f9;
  border-radius: 6px;
  color: #7e8299;
  font-weight: 600;
  padding: 24px;
  text-align: center;
}

.blocked-state {
  background: #fff4de;
  color: #895c00;
}

.checkout-summary {
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.checkout-summary span,
.totals-box span {
  color: #7e8299;
  display: block;
  font-weight: 600;
  margin-bottom: 4px;
}

.checkout-summary strong {
  color: #181c32;
  font-size: 1.05rem;
}

.checkout-section,
.checkout-panel {
  background: #fff;
  border: 1px solid #ebedf3;
  border-radius: 6px;
  padding: 20px;
}

.checkout-adjustments {
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.checkout-adjustments label {
  color: #7e8299;
  display: block;
  font-size: 0.78rem;
  font-weight: 800;
  margin-bottom: 6px;
  text-transform: uppercase;
}

.checkout-tables-grid {
  display: grid;
  gap: 16px;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
}

.itens-pendentes-scroll {
  max-height: 330px;
  overflow-y: auto;
}

.cobranca-itens-scroll {
  max-height: 330px;
  overflow-y: auto;
}

.itens-pendentes-scroll thead th,
.cobranca-itens-scroll thead th {
  background: #fff;
  position: sticky;
  top: 0;
  z-index: 1;
}

.table-compact th,
.table-compact td {
  padding-left: 10px;
  padding-right: 10px;
}

.cobranca-quantidade-cell {
  min-width: 92px;
  width: 92px;
}

.cobranca-quantidade-cell .form-control {
  min-height: 36px;
  padding: 6px 8px;
  text-align: right;
}

.cobranca-acoes-cell {
  min-width: 92px;
  width: 92px;
}

.item-action-buttons {
  align-items: center;
  display: inline-flex;
  gap: 8px;
  justify-content: flex-end;
  white-space: nowrap;
}

.item-action-buttons .btn {
  flex: 0 0 auto;
  height: 34px;
  width: 34px;
}

.item-action-buttons .mr-2 {
  margin-right: 0 !important;
}

.section-title {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 16px;
}

.section-title.compact {
  margin-bottom: 10px;
}

.section-title h4 {
  color: #181c32;
  font-size: 1.1rem;
  font-weight: 700;
  margin: 0;
}

.section-title span {
  color: #7e8299;
  font-weight: 600;
}

.checkout-comandas-grid {
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
}

.checkout-comanda {
  background: #f3f6f9;
  border: 1px solid #e4e6ef;
  border-radius: 6px;
  color: #7e8299;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  font-weight: 600;
  min-height: 82px;
  padding: 12px;
  text-align: left;
}

.checkout-comanda.active {
  background: #e8fff3;
  border-color: #1bc5bd;
}

.checkout-comanda strong {
  color: #181c32;
  margin-bottom: 4px;
}

.muted {
  opacity: 0.62;
}

.cobrancas-list {
  display: grid;
  gap: 10px;
}

.cobranca-line {
  align-items: center;
  background: #f3f6f9;
  border-radius: 6px;
  display: flex;
  justify-content: space-between;
  padding: 12px;
}

.cobranca-line span {
  color: #7e8299;
  display: block;
  font-weight: 600;
}

.empty-mini {
  background: #f3f6f9;
  border-radius: 6px;
  color: #7e8299;
  font-weight: 600;
  padding: 14px;
  text-align: center;
}

.ajuste-chip {
  border-radius: 4px;
  display: inline-block;
  margin-top: 4px;
  padding: 2px 6px;
}

.ajuste-chip-desconto {
  background: #e8fff3;
  color: #0bb783 !important;
}

.ajuste-chip-acrescimo {
  background: #fff4de;
  color: #ffa800 !important;
}

.totals-box {
  border-top: 1px solid #ebedf3;
  margin-top: 18px;
  padding-top: 14px;
}

.totals-box > div {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
}

.totals-box strong {
  color: #181c32;
}

.total-main {
  border-top: 1px solid #ebedf3;
  margin-top: 12px;
  padding-top: 14px;
}

.total-main strong {
  color: #1bc5bd;
  font-size: 1.35rem;
}

.restaurante-payment-add-button {
  min-height: 46px;
  width: 100%;
}

.checkout-draft-actions {
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.restaurante-payment-shortcuts {
  display: grid;
  gap: 8px;
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.restaurante-payment-shortcuts__item {
  background: #f3f6f9;
  border: 1px solid #e4e6ef;
  border-radius: 6px;
  color: #7e8299;
  font-weight: 700;
  min-height: 40px;
}

.restaurante-payment-shortcuts__item strong {
  color: #f56c5b;
  margin-right: 4px;
}

.restaurante-payment-list {
  border-top: 1px solid #ebedf3;
  max-height: 190px;
  overflow-y: auto;
  padding-top: 12px;
}

.restaurante-payment-item {
  align-items: center;
  background: #f3f6f9;
  border-radius: 6px;
  display: grid;
  gap: 12px;
  grid-template-columns: 1fr auto 34px;
  margin-bottom: 8px;
  padding: 10px 12px;
}

.restaurante-payment-item span {
  color: #7e8299;
  display: block;
  font-size: 0.85rem;
  font-weight: 600;
}

.restaurante-payment-summary {
  border-top: 1px solid #ebedf3;
  margin-top: 14px;
  padding: 18px 12px 4px;
}

.restaurante-payment-summary span,
.restaurante-payment-summary small {
  color: #f56c5b;
  display: block;
  font-weight: 700;
}

.restaurante-payment-summary strong {
  color: #181c32;
  display: block;
  font-size: 2rem;
  line-height: 1.1;
  margin: 8px 0;
}

.payment-balance-grid {
  display: grid;
  gap: 8px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  margin-top: 14px;
}

.payment-balance-grid div {
  background: #f3f6f9;
  border-radius: 6px;
  padding: 10px;
}

.payment-balance-grid span {
  color: #7e8299;
}

.payment-balance-grid strong {
  font-size: 1rem;
  margin: 4px 0 0;
}

.cancelled-list {
  display: grid;
  gap: 10px;
}

.cancelled-item {
  align-items: center;
  background: #f3f6f9;
  border-radius: 4px;
  color: #7e8299;
  display: grid;
  gap: 12px;
  grid-template-columns: 1fr auto auto;
  padding: 12px;
}

@media (max-width: 991.98px) {
  .checkout-actions {
    grid-template-columns: 1fr 1fr;
    margin-top: 18px;
  }

  .checkout-summary {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .checkout-flow,
  .checkout-adjustments {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .itens-pendentes-scroll {
    max-height: none;
  }

  .checkout-tables-grid {
    grid-template-columns: 1fr;
  }

  .restaurante-payment-shortcuts {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 575.98px) {
  .restaurante-checkout.is-fullscreen {
    padding: 4px;
  }

  .restaurante-checkout.is-fullscreen > .card {
    min-height: calc(100vh - 8px);
    min-height: calc(100dvh - 8px);
  }

  .checkout-actions,
  .checkout-summary,
  .checkout-flow,
  .checkout-adjustments,
  .checkout-draft-actions,
  .payment-balance-grid,
  .restaurante-payment-shortcuts {
    grid-template-columns: 1fr;
  }
}

/* Bloco "Dividir cobrança por pessoas" */
.checkout-dividir-cobranca {
  background: rgba(54, 153, 255, 0.06);
  border: 1px solid rgba(54, 153, 255, 0.2);
  border-radius: 10px;
  padding: 12px 14px;
  margin-bottom: 12px;
}
.checkout-dividir-titulo {
  font-weight: 700;
  font-size: 0.9rem;
  color: #181c32;
  margin-bottom: 8px;
}
.checkout-dividir-controles {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.checkout-dividir-input {
  max-width: 130px;
}
@media (max-width: 575.98px) {
  .checkout-dividir-input {
    max-width: 100%;
    flex: 1 1 100%;
  }
}
</style>

<style>
.restaurante-checkout-payment-modal .modal-dialog {
  max-width: calc(100vw - 24px);
  width: 820px;
}

.restaurante-checkout-payment-modal .modal-content {
  max-height: calc(100vh - 24px);
  max-height: calc(100dvh - 24px);
}

.restaurante-checkout-compact-modal .modal-dialog {
  max-width: calc(100vw - 24px);
}

.restaurante-checkout-payment-modal .restaurante-payment-modal-body {
  min-height: 0;
}

@media (max-width: 575.98px) {
  .restaurante-checkout-payment-modal .modal-dialog,
  .restaurante-checkout-compact-modal .modal-dialog {
    max-width: calc(100vw - 8px);
    width: calc(100vw - 8px);
  }
}
</style>
